import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { generateFramePromptsFromDescription, generateSceneImage, preloadProjectAssets, transcribeShotNarration } from '../services/geminiService';
import { rePollJob, generateVideo, generatePreProduction, preloadAvailableModels, getAllVideoModels, expandManualShot } from '../services/aiService';
import { buildShotVideoPayload, stampSelectedVideoModelOnShots, resolveSelectedVideoModel } from '../services/videoPayloadBuilder';
import { CREDIT_COSTS } from '../types';
import { parsePanelsFromDescription, detectEntityIdsInPanel } from '../utils/panelParser';
import { ImageEditorModal } from './ImageEditorModal';
import { CharacterPortraitModal } from './CharacterPortraitModal';
import { VideoPreProductionModal } from './VideoPreProductionModal';
import { AnimateVideoModal } from './AnimateVideoModal';
import { VideoEditorModal } from './VideoEditorModal';
import { VideoPreviewModal } from './VideoPreviewModal';
import { ShotAudioModal } from './ShotAudioModal';
import AIToolkitSelector from './AIToolkitSelector';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { ArrowLeftIcon } from './icons/ArrowLeftIcon';
import { ArrowRightIcon } from './icons/ArrowRightIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { FilmIcon } from './icons/FilmIcon';
import { PhotoIcon } from './icons/PhotoIcon';
import { PencilIcon } from './icons/PencilIcon';
import { UploadIcon } from './icons/UploadIcon';
import { TrashIcon } from './icons/TrashIcon';
import { MicrophoneIcon } from './icons/MicrophoneIcon';
import { PlayIcon } from './icons/PlayIcon';
import { PlusIcon } from './icons/PlusIcon';
import { EyeIcon } from './icons/EyeIcon';
import { CloseIcon } from './icons/CloseIcon';

export const Storyboard = forwardRef(({ projectData, onSubmit, onBack, onProjectUpdate, currentCredits, onDeductCredits, onTrackCreditsUsed, onRefreshCredits, onSaveMedia, onAutoSave, silent = false }, ref) => {
    const [scenes, setScenes] = useState(projectData.scenes || []);
    
    const scenesRef = useRef(scenes);
    useEffect(() => {
        scenesRef.current = scenes;
    }, [scenes]);

    // Preload available AI models on mount so the Pre-Production modal opens instantly
    useEffect(() => {
        preloadAvailableModels();
    }, []);

    // Re-stamp `shot.preProduction.selectedVideoModel` across all shots
    // Keep selectedVideoModel on every shot in sync with the AI Toolkit AND
    // with the user's tier selection. There are TWO trigger paths:
    //
    //   • On mount (eager restamp): if the user changed tier / AI Toolkit
    //     selection while Storyboard was NOT mounted (typical flow: Tier
    //     Selection → Continue to Storyboard), the existing stamps may
    //     reflect an older tier's model. Restamp once on mount so the
    //     stamps match the CURRENT localStorage before any Generate Video
    //     button is clicked.
    //
    //   • On live events (`ai-models-changed`, `storage`): keeps stamps in
    //     sync while the user is on the Storyboard screen and changes
    //     models via the AI Toolkit modal.
    //
    // See bug Feb 24, 2026: Economy tier was respected by the Tier Selection
    // UI but ignored at video-generation time because shots had been
    // pre-stamped with the Premium refs-to-video model during an earlier
    // Pre-Production pass — and the stamps survived the tier change.
    useEffect(() => {
        const restamp = async () => {
            try {
                const latestScenes = scenesRef.current || [];
                const updated = await Promise.all(latestScenes.map(async (scene) => {
                    const shotsNeedingRestamp = scene.shots.filter(sh => sh.preProduction);
                    if (shotsNeedingRestamp.length === 0) return scene;
                    const stampedShots = await stampSelectedVideoModelOnShots(
                        shotsNeedingRestamp.map(sh => ({
                            id: sh.id,
                            endFrameImage: sh.endFrameImage,
                            // Pass panels through so the legacy `selectedVideoModel`
                            // is computed correctly (panel-based shots prefer the
                            // refs-to-video variant).
                            panels: sh.panels,
                            preProduction: sh.preProduction
                        }))
                    );
                    const byId = new Map(stampedShots.map(s => [s.id, s]));
                    return {
                        ...scene,
                        shots: scene.shots.map(sh => {
                            const ss = byId.get(sh.id);
                            return ss ? { ...sh, preProduction: ss.preProduction } : sh;
                        })
                    };
                }));
                syncToParent(updated);
                console.log('🎬 Re-stamped selectedVideoModel across all shots (mount or AI Toolkit change)');
            } catch (e) {
                console.warn('Re-stamp after AI Toolkit change failed:', e);
            }
        };
        // Eager restamp on mount to catch tier changes made while Storyboard
        // was unmounted (the most common bug scenario).
        restamp();
        const onAIChange = () => { restamp(); };
        const onStorage = (e) => { if (e.key === 'selectedAIModels') restamp(); };
        window.addEventListener('ai-models-changed', onAIChange);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('ai-models-changed', onAIChange);
            window.removeEventListener('storage', onStorage);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [editingInfo, setEditingInfo] = useState(null);
    const [editingCharacter, setEditingCharacter] = useState(null);
    const [preppingVideoForShot, setPreppingVideoForShot] = useState(null);
    const [previewingVideoUrl, setPreviewingVideoUrl] = useState(null);
    const [managingAudioForShot, setManagingAudioForShot] = useState(null);
    const [animatingShot, setAnimatingShot] = useState(null);
    const [editingVideoForShot, setEditingVideoForShot] = useState(null);
    const [updatingPromptsForShotId, setUpdatingPromptsForShotId] = useState(null);
    const [generatingFrames, setGeneratingFrames] = useState({});
    const [generatingScenes, setGeneratingScenes] = useState(new Set());
    const [generatingVideosForScenes, setGeneratingVideosForScenes] = useState(new Set());
    const [sceneErrors, setSceneErrors] = useState({});
    const [isGeneratingAll, setIsGeneratingAll] = useState(false);
    const [isGeneratingAllVideos, setIsGeneratingAllVideos] = useState(false);
    const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, message: '' });
    const [editingCharacterForShot, setEditingCharacterForShot] = useState(null);
    // Feb 2026 — Tracks a pending "+ Create new" flow initiated from a
    // specific panel's Add dropdown. When set, saving the character in
    // the modal also attaches it to that panel automatically.
    // Shape: { sceneId, shotId, panelIndex, category }
    const [pendingCreateForPanel, setPendingCreateForPanel] = useState(null);
    const [videoGenerationProgress, setVideoGenerationProgress] = useState({});
    const [dismissedElements, setDismissedElements] = useState({}); // { shotId: Set([elementId, ...]) }
    const [viewingFrameImage, setViewingFrameImage] = useState(null); // { url: string, title: string }
    const [showAIToolkitModal, setShowAIToolkitModal] = useState(false); // AI Toolkit modal state
    const [preProductionStatus, setPreProductionStatus] = useState({}); // { sceneId: 'pending' | 'loading' | 'ready' | 'failed' }
    const [editingDescriptionShotId, setEditingDescriptionShotId] = useState(null); // shot.id being edited
    const [editingPromptKey, setEditingPromptKey] = useState(null); // 'shotId-start' or 'shotId-end'
    const videoInputRefs = useRef({});
    const frameInputRefs = useRef({});
    const debounceTimeoutRef = useRef(null);

    const selectedModelName = projectData.modelPreferences?.video?.general;
    const requiresStartFrame = true;
    const requiresEndFrame = selectedModelName?.toLowerCase() === 'veo 3.1';

    const syncToParent = (updatedScenes) => {
        console.log('🔄 syncToParent called with', updatedScenes.length, 'scenes');
        updatedScenes.forEach((scene, sIdx) => {
            scene.shots.forEach((shot, shotIdx) => {
                if (shot.startFrameImage || shot.endFrameImage) {
                    console.log(`  Scene ${sIdx + 1}, Shot ${shotIdx + 1}: startFrame=${shot.startFrameImage ? 'YES' : 'NO'}, endFrame=${shot.endFrameImage ? 'YES' : 'NO'}`);
                }
            });
        });
        scenesRef.current = updatedScenes;
        setScenes(updatedScenes);
        onProjectUpdate({ ...projectData, scenes: updatedScenes });
    };

    // Merge only one scene into the latest state (prevents overwriting other scenes)
    const syncSceneToParent = (sceneId, updatedScene) => {
        const latest = scenesRef.current;
        const merged = latest.map(s => s.id === sceneId ? updatedScene : s);
        syncToParent(merged);
    };

    const handleShotUpdate = (sceneId, shotId, field, value) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                const shotIndex = scene.shots.findIndex(s => s.id === shotId);
                
                return {
                    ...scene,
                    shots: scene.shots.map((shot, idx) => {
                        if (shot.id === shotId) {
                            return { ...shot, [field]: value };
                        }
                        
                        // CONTINUITY RULE: When end frame of shot N changes, 
                        // automatically update start frame of shot N+1
                        if (field === 'endFrameImage' && idx === shotIndex + 1) {
                            console.log(`🔗 Continuity: Propagating end frame from Shot ${shotIndex + 1} to start frame of Shot ${idx + 1}`);
                            return { ...shot, startFrameImage: value };
                        }
                        
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    // Helper to update multiple fields on a shot atomically (avoid race conditions)
    const handleShotUpdateMultiple = (sceneId, shotId, updates) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map((shot) => {
                        if (shot.id === shotId) {
                            return { ...shot, ...updates };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        console.log(`📹 Updated shot ${shotId} with:`, Object.keys(updates).join(', '));
        syncToParent(newScenes);
    };

    const updateFramePrompts = async (sceneId, shotId, newDescription) => {
        if (!newDescription.trim()) {
            setUpdatingPromptsForShotId(null);
            return;
        }
        setUpdatingPromptsForShotId(shotId);
        try {
            // Gather context for cinematic prompt generation
            const scene = scenes.find(s => s.id === sceneId);
            const shot = scene?.shots?.find(s => s.id === shotId);
            const shotIndex = scene?.shots?.findIndex(s => s.id === shotId) ?? 0;
            const sceneIndex = scenes.findIndex(s => s.id === sceneId);
            const characters = projectData.characters || [];
            const shotCharacters = (shot?.characterIds || [])
                .map(id => characters.find(c => c.id === id))
                .filter(Boolean);
            const script = projectData.script || '';

            const context = {
                description: newDescription,
                shotIndex: shotIndex + 1,
                sceneIndex: sceneIndex + 1,
                totalShots: scene?.shots?.length || 1,
                characters: shotCharacters.map(c => ({ name: c.name, description: c.description })),
                scriptSnippet: script.length > 600 ? script.substring(0, 600) + '...' : script,
                cameraAngle: shot?.cameraAngle || '',
                vfx: shot?.vfx || '',
                mood: shot?.mood || '',
            };

            const { startFramePrompt, endFramePrompt } = await generateFramePromptsFromDescription(newDescription, context);
            
            setScenes(prevScenes => {
                const newScenes = prevScenes.map(scene => {
                    if (scene.id === sceneId) {
                        return {
                            ...scene,
                            shots: scene.shots.map(shot => {
                                if (shot.id === shotId) {
                                    return { ...shot, startFramePrompt, endFramePrompt };
                                }
                                return shot;
                            })
                        };
                    }
                    return scene;
                });
                
                setTimeout(() => onProjectUpdate({ ...projectData, scenes: newScenes }), 0);
                return newScenes;
            });
        } catch (err) {
            console.error("Failed to generate prompts:", err);
        } finally {
            setUpdatingPromptsForShotId(null);
        }
    };

    const handleDescriptionChange = (sceneId, shotId, newDescription) => {
        handleShotUpdate(sceneId, shotId, 'description', newDescription);
    };

    const handleDescriptionSubmit = (sceneId, shotId, description) => {
        setEditingDescriptionShotId(null);
        if (description && description.trim()) {
            updateFramePrompts(sceneId, shotId, description);
        }
    };

    // Feb 2026 — Manual-shot flag. A shot is "manual" when its description
    // does not (yet) contain the "Panel N (HH:MM…)" pattern the LLM emits.
    // Only manual shots get the "How many panels?" picker; AI-generated
    // shots already carry structured panels.
    const isManualShot = (shot) => {
        const desc = String(shot?.description || '');
        if (!desc.trim()) return true;
        return !/Panel\s*\d+\s*\(\s*\d{1,2}:\d{2}/i.test(desc);
    };

    // How many panels the user wants for each manual shot they expand.
    // Keyed by shotId. Defaults to 4 when not set.
    const [manualPanelCounts, setManualPanelCounts] = useState({});
    const [expandingManualShotId, setExpandingManualShotId] = useState(null);

    const handleExpandManualShot = async (sceneId, shotId) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot = scene?.shots.find(sh => sh.id === shotId);
        if (!shot || !shot.description || !shot.description.trim()) {
            alert('Please add a description first before generating panels.');
            return;
        }
        // Existing panels? Confirm overwrite.
        const parsedPanels = parsePanelsFromDescription(shot.description);
        if (parsedPanels.length > 0) {
            const ok = window.confirm(`This will replace ${parsedPanels.length} existing panel${parsedPanels.length === 1 ? '' : 's'}. Continue?`);
            if (!ok) return;
        }
        const numPanels = Math.max(1, Math.min(8, Number(manualPanelCounts[shotId]) || 4));
        setExpandingManualShotId(shotId);
        try {
            // Feb 2026 — Prefer the shot's ACTUALLY-attached characters/elements
            // (falling back to all project characters when nothing is attached yet)
            // so the cinematic description references the right names.
            const allProjectChars = projectData?.characters || [];
            const attachedCharIds = shot.characterIds || [];
            const attachedElemIds = shot.elementIds || [];
            const attachedChars = allProjectChars.filter(c =>
                c.category !== 'element' && (attachedCharIds.includes(c.id) || attachedCharIds.length === 0 && attachedElemIds.length === 0)
            );
            const attachedElems = allProjectChars.filter(c =>
                c.category === 'element' && (attachedElemIds.includes(c.id) || attachedCharIds.length === 0 && attachedElemIds.length === 0)
            );

            const res = await expandManualShot(shot.description, numPanels, {
                characters: allProjectChars,
                elements: allProjectChars.filter(c => c.category === 'element'),
                shotCharacters: attachedChars,
                shotElements: attachedElems,
                projectMetadata: {
                    aspectRatio: projectData?.aspectRatio,
                    videoStyle: projectData?.videoStyle,
                    duration: projectData?.duration,
                    tone: projectData?.tone,
                    industry: projectData?.industry,
                },
                sceneTitle: scene?.title || '',
            });
            if (!res.success || !res.shot) {
                alert(`Panel generation failed: ${res.error || 'Unknown error'}`);
                return;
            }
            // Merge fields from the returned shot onto the existing one.
            // Preserve the original shot.id and any user-attached ids.
            // CRITICAL: `res.shot.description` now carries the fully-formatted
            // cinematic breakdown (opener + N panel blocks) — spread ORDER
            // matters so it wins over the user's original raw draft.
            // Also reset `panels` so the storyboard reseeds from the new
            // description via getPanelsForShot().
            const merged = { ...shot, ...res.shot, id: shot.id, panels: [] };
            const newScenes = scenes.map(sc => {
                if (sc.id !== sceneId) return sc;
                return { ...sc, shots: sc.shots.map(sh => sh.id === shotId ? merged : sh) };
            });
            syncToParent(newScenes);
        } finally {
            setExpandingManualShotId(null);
        }
    };

    const handlePromptEdit = (sceneId, shotId, frameType, newPrompt) => {
        const field = frameType === 'start' ? 'startFramePrompt' : 'endFramePrompt';
        handleShotUpdate(sceneId, shotId, field, newPrompt);
        setEditingPromptKey(null);
    };

    const handleAddScene = () => {
        const newScene = {
            id: uuidv4(),
            shots: [
                {
                    id: uuidv4(),
                    description: '',
                    characterIds: [],
                    elementIds: []
                }
            ]
        };
        syncToParent([...scenes, newScene]);
    };

    const handleAddShot = (sceneId) => {
        const newShot = {
            id: uuidv4(),
            description: '',
            characterIds: [],
            elementIds: []
        };
        
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return { ...scene, shots: [...scene.shots, newShot] };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleRemoveShot = (sceneId, shotId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.filter(s => s.id !== shotId)
                };
            }
            return scene;
        }).filter(scene => scene.shots.length > 0);
        syncToParent(newScenes);
    };

    const handleRemoveScene = (sceneId) => {
        const newScenes = scenes.filter(s => s.id !== sceneId);
        syncToParent(newScenes);
    };

    const handleAddCharacterToShot = (sceneId, shotId, characterId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id === shotId) {
                            const currentIds = shot.characterIds || [];
                            if (currentIds.includes(characterId)) return shot;
                            return { ...shot, characterIds: [...currentIds, characterId], entitiesLocked: true };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleRemoveCharacterFromShot = (sceneId, shotId, characterId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id === shotId) {
                            return { ...shot, characterIds: shot.characterIds?.filter(id => id !== characterId) || [], entitiesLocked: true };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleAddElementToShot = (sceneId, shotId, elementId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id === shotId) {
                            const currentIds = shot.elementIds || [];
                            if (currentIds.includes(elementId)) return shot;
                            return { ...shot, elementIds: [...currentIds, elementId], entitiesLocked: true };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleRemoveElementFromShot = (sceneId, shotId, elementId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id === shotId) {
                            return { ...shot, elementIds: shot.elementIds?.filter(id => id !== elementId) || [], entitiesLocked: true };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleCreateNewCharacter = (category) => {
        const newChar = {
            id: uuidv4(),
            name: category === 'character' ? 'New Character' : 'New Element',
            description: '',
            category: category,
            referenceImages: [],
            costumeAndAccessoryImages: [],
        };
        
        setEditingCharacter(newChar);
    };

    const handleSaveCharacter = (updatedChar, sceneId, shotId) => {
        console.log('💾 handleSaveCharacter called:', {
            charId: updatedChar.id,
            name: updatedChar.name,
            category: updatedChar.category,
            hasImage: !!updatedChar.image,
            sceneId,
            shotId
        });
        
        // Check if this is a new character (not in project yet)
        const isNew = !projectData.characters.find(c => c.id === updatedChar.id);
        
        let updatedChars;
        if (isNew) {
            updatedChars = [...projectData.characters, updatedChar];
        } else {
            updatedChars = projectData.characters.map(c => c.id === updatedChar.id ? updatedChar : c);
        }
        
        // If we need to add to a shot, do BOTH updates atomically to avoid stale closure overwrite
        if (sceneId && shotId && isNew) {
            const idField = updatedChar.category === 'element' ? 'elementIds' : 'characterIds';
            const updatedScenes = scenes.map(scene => {
                if (scene.id === sceneId) {
                    return {
                        ...scene,
                        shots: scene.shots.map(shot => {
                            if (shot.id === shotId) {
                                const currentIds = shot[idField] || [];
                                if (currentIds.includes(updatedChar.id)) return shot;
                                return { ...shot, [idField]: [...currentIds, updatedChar.id] };
                            }
                            return shot;
                        })
                    };
                }
                return scene;
            });
            setScenes(updatedScenes);
            // Single atomic update: characters + scenes together
            onProjectUpdate({ ...projectData, characters: updatedChars, scenes: updatedScenes });
            console.log('  Saved new character + added to shot in one update');
        } else if (pendingCreateForPanel && isNew) {
            // Feb 2026 — "+ Create new" from a panel Add dropdown. Attach
            // the newly created character/element to the specific panel it
            // was created FROM, in one atomic project update.
            const { sceneId: panelSceneId, shotId: panelShotId, panelIndex, category } = pendingCreateForPanel;
            const idField = category === 'element' ? 'elementIds' : 'characterIds';
            const updatedScenes = scenes.map(scene => {
                if (scene.id !== panelSceneId) return scene;
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id !== panelShotId) return shot;
                        const panels = Array.isArray(shot.panels) ? [...shot.panels] : [];
                        if (panels[panelIndex]) {
                            const panel = { ...panels[panelIndex] };
                            const existing = Array.isArray(panel[idField]) ? panel[idField] : [];
                            if (!existing.includes(updatedChar.id)) {
                                panel[idField] = [...existing, updatedChar.id];
                            }
                            panels[panelIndex] = panel;
                        }
                        return { ...shot, panels };
                    }),
                };
            });
            setScenes(updatedScenes);
            onProjectUpdate({ ...projectData, characters: updatedChars, scenes: updatedScenes });
            console.log(`  Saved new ${category} + attached to panel ${panelIndex}`);
            setPendingCreateForPanel(null);
        } else {
            onProjectUpdate({ ...projectData, characters: updatedChars });
        }
        
        setEditingCharacter(null);
    };

    const handleGenerateSceneVideos = async (sceneId) => {
        const targetScene = scenesRef.current.find(s => s.id === sceneId);
        if (!targetScene) return;

        // Give clear feedback instead of silently no-op-ing.
        const sceneIdxForMsg = scenesRef.current.findIndex(s => s.id === sceneId) + 1;
        // Panel-aware: a shot is "ready for video" if it has a legacy start
        // frame OR at least one panel image. This way panel-based projects
        // pass the Gen Videos guard without ever populating startFrameImage.
        const shotsMissingStart = targetScene.shots.filter(s => !hasGenerableVideoSource(s));
        const shotsAlreadyHaveVideo = targetScene.shots.filter(s => hasGenerableVideoSource(s) && s.video);
        const shotsNeedingVideos = targetScene.shots.filter(s => hasGenerableVideoSource(s) && !s.video);

        if (shotsNeedingVideos.length === 0) {
            // Nothing to do with default behaviour — explain clearly.
            if (targetScene.shots.length === 0) {
                if (!silent) alert(`Scene ${sceneIdxForMsg} has no shots.`);
                return;
            }
            if (shotsAlreadyHaveVideo.length === targetScene.shots.length) {
                if (!silent) alert(
                    `Videos are already present for Scene ${sceneIdxForMsg}. ` +
                    `All ${shotsAlreadyHaveVideo.length} shot(s) already have videos.\n\n` +
                    `Tip: to regenerate a shot's video, delete it from the shot card and click Gen Videos again.`
                );
                return;
            }
            if (shotsMissingStart.length > 0) {
                if (!silent) alert(
                    `Cannot generate videos for Scene ${sceneIdxForMsg}:\n\n` +
                    `${shotsMissingStart.length} shot(s) don't have a start frame image yet.\n` +
                    `Click "Gen Images" for this scene first, or generate frames individually.`
                );
                return;
            }
            if (!silent) alert(`Nothing to generate for Scene ${sceneIdxForMsg}.`);
            return;
        } else if (shotsMissingStart.length > 0) {
            // Some eligible, some missing frames — warn but continue with eligible ones.
            console.warn(
                `Scene ${sceneIdxForMsg}: skipping ${shotsMissingStart.length} shot(s) without start frames. ` +
                `Generating videos for ${shotsNeedingVideos.length} eligible shot(s).`
            );
        }

        setGeneratingVideosForScenes(prev => new Set(prev).add(sceneId));
        setSceneErrors(prev => ({ ...prev, [sceneId]: null }));

        const failedVideos = [];
        let targetSceneState = { ...scenesRef.current.find(s => s.id === sceneId) };

        // ---- Resolve the user-selected video model ----
        // Each shot was stamped with THREE variants during pre-production:
        //   • selectedVideoModelImageToVideo
        //   • selectedVideoModelStartEndFrame
        //   • selectedVideoModelReferencesToVideo  (for panel-based shots)
        // We pick the correct stamped variant based on the shot's CURRENT
        // state (panel-based shots win over end-frame which wins over image).
        // Falls back to the live resolver only if no matching stamp is
        // present (legacy projects) or if the chosen variant is missing.
        const resolveSelectedModel = async (shot, hasEndFrame, isReferencesToVideo = false) => {
            const pp = shot?.preProduction || {};
            if (isReferencesToVideo) {
                // Always live-resolve so the user's CURRENT tier selection is
                // authoritative — stamps go stale when the user picks a tier
                // while Storyboard is unmounted (the mount-time restamp catches
                // most cases, but not race conditions where Generate Video
                // fires before restamp completes). Live resolution is cheap:
                // `getAllVideoModels()` is cached in-process.
                const live = await resolveSelectedVideoModel(false, true);
                if (live?.id) return live.id;
                // Live resolver returned null (no refs-to-video models in the
                // admin catalog at all). Fall back to a v2+ stamp if one
                // exists — older v1 stamps are rejected because they may
                // carry a start-end-frame model id.
                const stamped = pp.selectedVideoModelReferencesToVideo;
                if (stamped?.id && stamped.isReferencesToVideo && stamped.stampVersion >= 2) return stamped.id;
                // No refs-to-video model available anywhere — surface a clear
                // error instead of silently using 'kling' (image-to-video)
                // which would cause the backend to ignore reference_images[].
                throw new Error(
                    'No references-to-video model is configured in your AI Toolkit. ' +
                    'Open AI Toolkit and select a "References to video" model, ' +
                    'or contact your admin to enable refs-to-video capability.'
                );
            }
            const stamped = hasEndFrame
                ? (pp.selectedVideoModelStartEndFrame || pp.selectedVideoModelImageToVideo)
                : (pp.selectedVideoModelImageToVideo || pp.selectedVideoModelStartEndFrame);
            // Only trust a stamp whose hasEndFrame matches what we need.
            // Also reject stamps that carry the refs-to-video flag — they
            // belong to the panel-based code path.
            if (stamped?.id && stamped.hasEndFrame === hasEndFrame && !stamped.isReferencesToVideo) return stamped.id;
            // Legacy single-stamp field
            const legacy = pp.selectedVideoModel;
            if (legacy?.id && legacy.hasEndFrame === hasEndFrame && !legacy.isReferencesToVideo) return legacy.id;
            // Fall back to live resolution
            const resolved = await resolveSelectedVideoModel(hasEndFrame);
            return resolved?.id || 'kling';
        };

        // SEQUENTIAL per-shot loop. Calling the same flow as the individual
        // "Generate Video" button — build payload via the shared
        // `buildShotVideoPayload` helper (used by the modal too) so language,
        // dialogue timing, character actions, audio cues and text overlays
        // are bit-for-bit identical between bulk and individual generation.
        for (const shot of shotsNeedingVideos) {
            const sceneIdx = scenesRef.current.findIndex(s => s.id === sceneId);
            const shotIdx = targetScene.shots.findIndex(s => s.id === shot.id);
            console.log(`🎬 Generating video: Scene ${sceneIdx + 1}, Shot ${shotIdx + 1}`);

            try {
                const charactersInShot = getCharactersForShot(shot);
                const payload = buildShotVideoPayload(shot, projectData, charactersInShot);
                const hasEndFrame = !!payload.endFrameImage;
                const isRefsToVideo = !!payload.isReferencesToVideo;
                const modelKey = await resolveSelectedModel(shot, hasEndFrame, isRefsToVideo);

                console.log(`  🎥 Model: ${modelKey}, duration: ${payload.duration}s, hasEndFrame: ${hasEndFrame}, refsToVideo: ${isRefsToVideo}, refImages: ${payload.referenceImages?.length || 0}`);
                console.log(`     dialogues: ${payload.dialoguesData.length}, cinematic_actions: ${payload.cinematicActions.length}`);
                console.log(`     promptLen: ${payload.finalPrompt.length} chars`);

                const result = await generateVideo(
                    payload.finalPrompt,
                    payload.duration,
                    modelKey,
                    payload.aspectRatio,
                    payload.startFrameImage,
                    payload.endFrameImage,
                    null,
                    payload.dialoguesData.length > 0 ? payload.dialoguesData : null,
                    payload.cinematicActions.length > 0 ? payload.cinematicActions : null,
                    payload.referenceImages && payload.referenceImages.length > 0 ? payload.referenceImages : null
                );
                const videoUrl = result?.videoUrl || result?.video_url;

                if (onTrackCreditsUsed && result?.creditsUsed && result.creditsUsed > 0) {
                    onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
                }
                if (videoUrl) {
                    targetSceneState = {
                        ...targetSceneState,
                        shots: targetSceneState.shots.map(s => s.id === shot.id ? { ...s, video: videoUrl } : s)
                    };
                    syncSceneToParent(sceneId, targetSceneState);
                    console.log(`  ✅ Video generated for Scene ${sceneIdx + 1}, Shot ${shotIdx + 1}`);
                }
            } catch (err) {
                console.warn(`⚠️ Video failed for Shot ${shotIdx + 1}:`, err.message);
                failedVideos.push({ shotId: shot.id, sceneId, jobId: err.jobId || null, shot });
            }
        }

        // Smart recovery for failed videos: re-fetch first, re-generate as last resort
        for (const fv of failedVideos) {
            let recovered = false;

            if (fv.jobId) {
                console.log(`🔄 Re-fetching video for shot ${fv.shotId} via jobId ${fv.jobId}...`);
                try {
                    const rePollResult = await rePollJob(fv.jobId, 15, 5000);
                    const videoUrl = rePollResult.result?.video_url || rePollResult.result?.result?.video_url || rePollResult.imageUrl;
                    if (rePollResult.success && videoUrl) {
                        targetSceneState = {
                            ...targetSceneState,
                            shots: targetSceneState.shots.map(s => s.id === fv.shotId ? { ...s, video: videoUrl } : s)
                        };
                        syncSceneToParent(sceneId, targetSceneState);
                        recovered = true;
                        console.log(`✅ Re-fetched video for shot ${fv.shotId}`);
                    }
                } catch { /* re-fetch failed */ }
            }

            if (!recovered) {
                console.log(`🔄 Re-generating video for shot ${fv.shotId}...`);
                const shot = fv.shot;
                const charactersInShot = getCharactersForShot(shot);
                const payload = buildShotVideoPayload(shot, projectData, charactersInShot);
                const modelKey = await resolveSelectedModel(shot, payload.hasEndFrame, !!payload.isReferencesToVideo);
                try {
                    const result = await generateVideo(
                        payload.finalPrompt,
                        payload.duration,
                        modelKey,
                        payload.aspectRatio,
                        payload.startFrameImage,
                        payload.endFrameImage,
                        null,
                        payload.dialoguesData.length > 0 ? payload.dialoguesData : null,
                        payload.cinematicActions.length > 0 ? payload.cinematicActions : null,
                        payload.referenceImages && payload.referenceImages.length > 0 ? payload.referenceImages : null
                    );
                    const videoUrl = result?.videoUrl || result?.video_url;
                    if (onTrackCreditsUsed && result?.creditsUsed && result.creditsUsed > 0) {
                        onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
                    }
                    if (videoUrl) {
                        targetSceneState = {
                            ...targetSceneState,
                            shots: targetSceneState.shots.map(s => s.id === fv.shotId ? { ...s, video: videoUrl } : s)
                        };
                        syncSceneToParent(sceneId, targetSceneState);
                        console.log(`✅ Re-generated video for shot ${fv.shotId}`);
                    }
                } catch (regenErr) {
                    console.error(`❌ Re-generate video also failed for shot ${fv.shotId}:`, regenErr.message);
                }
            }
        }

        setGeneratingVideosForScenes(prev => {
            const newSet = new Set(prev);
            newSet.delete(sceneId);
            return newSet;
        });
        // Stage 5: auto-save after scene video generation completes (300ms delay
        // for React to commit scene updates before autosave reads the parent ref).
        if (onAutoSave) setTimeout(() => onAutoSave({ reason: `scene-videos:${sceneId}` }), 300);
    };

    /**
     * Adjust a frame prompt to stay in sync with the shot's confirmed
     * characters/elements. Two directions:
     *
     * 1) REMOVED (name in prompt, not in chips): strip the name and append
     *    a negative instruction so the image model doesn't hallucinate a
     *    replacement character to fill the role.
     *
     * 2) ADDED (in chips, name NOT in prompt): append a positive
     *    instruction so the image model knows to include the added
     *    character (their portrait is already passed as a reference, but
     *    without a prompt mention the model often ignores it or places
     *    them arbitrarily).
     */
    const sanitizePromptForConfirmedEntities = (basePrompt, shot, confirmedIdsOverride = null) => {
        if (!basePrompt || !projectData.characters) return basePrompt;
        // When `confirmedIdsOverride` is supplied (e.g., per-panel cast), use it.
        // Otherwise default to the shot-level confirmed set.
        const confirmedCharIds = new Set(
            confirmedIdsOverride?.characterIds ?? (shot.characterIds || [])
        );
        const confirmedElemIds = new Set(
            confirmedIdsOverride?.elementIds ?? (shot.elementIds || [])
        );

        // Helper: is an entity's name present in the text (word-boundary,
        // case-insensitive, with first-name fallback ≥4 chars)?
        const nameAppearsIn = (entity, text) => {
            const name = (entity.name || '').trim();
            if (!name) return false;
            const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            if (new RegExp(`\\b${esc}\\b`, 'i').test(text)) return true;
            const first = name.split(/\s+/)[0];
            if (first && first.length >= 4 && first.toLowerCase() !== name.toLowerCase()) {
                const firstEsc = first.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                if (new RegExp(`\\b${firstEsc}\\b`, 'i').test(text)) return true;
            }
            return false;
        };

        const visibleEntities = projectData.characters.filter(e =>
            (e.category ?? 'character') !== 'narrator'
        );
        const removedEntities = visibleEntities.filter(e => {
            const cat = e.category ?? 'character';
            return cat === 'element' ? !confirmedElemIds.has(e.id) : !confirmedCharIds.has(e.id);
        });
        const confirmedEntities = visibleEntities.filter(e => {
            const cat = e.category ?? 'character';
            return cat === 'element' ? confirmedElemIds.has(e.id) : confirmedCharIds.has(e.id);
        });

        let sanitized = basePrompt;

        // 1) REMOVAL handling — strip names + negative instruction
        const actuallyExcluded = [];
        for (const entity of removedEntities) {
            if (!nameAppearsIn(entity, sanitized)) continue;
            const name = entity.name.trim();
            const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            sanitized = sanitized.replace(new RegExp(`\\b${esc}\\b`, 'gi'), '');
            const first = name.split(/\s+/)[0];
            if (first && first.length >= 4 && first.toLowerCase() !== name.toLowerCase()) {
                const firstEsc = first.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                sanitized = sanitized.replace(new RegExp(`\\b${firstEsc}\\b`, 'gi'), '');
            }
            sanitized = sanitized.replace(/\s{2,}/g, ' ').replace(/\s+,/g, ',').trim();
            actuallyExcluded.push(name);
        }
        if (actuallyExcluded.length > 0) {
            sanitized = `${sanitized} Do not include ${actuallyExcluded.join(', ')} in this frame. The scene must not feature ${actuallyExcluded.join(', ')}.`;
            console.log(`🧹 Sanitized prompt — excluded: ${actuallyExcluded.join(', ')}`);
        }

        // 2) ADDITION handling — append positive instruction for confirmed
        //    entities that the original prompt text didn't mention (their
        //    portrait/element image is passed as a reference but the prompt
        //    didn't describe their role, so the image model often ignores or
        //    misplaces them).
        const missingFromPrompt = [];
        const missingElements = [];
        for (const entity of confirmedEntities) {
            if (nameAppearsIn(entity, basePrompt)) continue;
            const cat = entity.category ?? 'character';
            if (cat === 'element') missingElements.push(entity.name);
            else missingFromPrompt.push(entity.name);
        }
        const additionClauses = [];
        if (missingFromPrompt.length > 0) {
            additionClauses.push(`Also include ${missingFromPrompt.join(', ')} in this frame. Use the provided reference ${missingFromPrompt.length > 1 ? 'images' : 'image'} for ${missingFromPrompt.length > 1 ? 'their' : 'their'} appearance.`);
        }
        if (missingElements.length > 0) {
            additionClauses.push(`Also include ${missingElements.join(', ')} visibly in this frame. Use the provided reference image${missingElements.length > 1 ? 's' : ''} for accurate appearance.`);
        }
        if (additionClauses.length > 0) {
            sanitized = `${sanitized} ${additionClauses.join(' ')}`;
            console.log(`➕ Added to prompt — confirmed but missing: ${[...missingFromPrompt, ...missingElements].join(', ')}`);
        }

        return sanitized;
    };

    const handleGenerateFrame = async (sceneId, shotId, frameType) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot = scene?.shots.find(s => s.id === shotId);
        if (!shot) return;

        const prompt = frameType === 'start' ? shot.startFramePrompt : shot.endFramePrompt;
        if (!prompt) {
            alert('Please add a shot description first to generate prompts');
            return;
        }

        const key = `${shotId}-${frameType}`;
        setGeneratingFrames(prev => ({ ...prev, [key]: true }));

        try {
            // Get ONLY characters matched to THIS shot
            const chars = getCharactersForShot(shot);
            // Get ONLY confirmed elements (explicitly added via elementIds) — not smart-matched suggestions
            const confirmedElements = (projectData.characters?.filter(c => c.category === 'element') || [])
                .filter(el => shot.elementIds?.includes(el.id));
            // Entities carry their own .image and .costumeAndAccessoryImages
            const allEntities = [...chars, ...confirmedElements];

            // Only additional refs: start frame for end-frame continuity
            const refs = [];
            if (frameType === 'end' && shot.startFrameImage) {
                refs.push(shot.startFrameImage);
            }

            const hasReferenceImages = refs.length > 0 || allEntities.some(e => e.image || e.costumeAndAccessoryImages?.length);
            const modelId = projectData.modelPreferences?.image?.general?.[hasReferenceImages ? 'referenceToImage' : 'textToImage'];

            console.log('🖼️ Frame generation:', {
                frameType, shotId,
                characters: chars.map(c => ({ name: c.name, hasImage: !!c.image, costumes: c.costumeAndAccessoryImages?.length || 0 })),
                confirmedElements: confirmedElements.map(e => ({ name: e.name, hasImage: !!e.image })),
                continuityRef: refs.length,
                modelType: hasReferenceImages ? 'referenceToImage' : 'textToImage',
            });

            const result = await generateSceneImage(sanitizePromptForConfirmedEntities(prompt, shot), projectData.aspectRatio, allEntities, refs, modelId);
            const image = result.image || result; // Handle both new and old format
            
            console.log('🖼️ generateSceneImage result:', {
                hasImage: !!image,
                creditsUsed: result.creditsUsed,
                creditsRemaining: result.creditsRemaining,
                jobId: result.jobId
            });
            
            const field = frameType === 'start' ? 'startFrameImage' : 'endFrameImage';
            handleShotUpdate(sceneId, shotId, field, image);
            
            // Track credits used from backend response (including remaining credits and job ID)
            if (onTrackCreditsUsed) {
                console.log('💳 Calling onTrackCreditsUsed with:', result.creditsUsed, result.creditsRemaining, result.jobId);
                if (result.creditsUsed > 0) {
                    onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
                }
            } else {
                console.warn('⚠️ onTrackCreditsUsed is not defined');
            }
            
            if (onSaveMedia) onSaveMedia([image]);
        } catch (err) {
            console.error('Frame generation failed:', err);
            alert(`Failed to generate ${frameType} frame: ${err.message}`);
        } finally {
            setGeneratingFrames(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleFrameImageUpload = (e, sceneId, shotId, frameType) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const field = frameType === 'start' ? 'startFrameImage' : 'endFrameImage';
            handleShotUpdate(sceneId, shotId, field, reader.result);
        };
        reader.readAsDataURL(file);

        if (e.target) e.target.value = '';
    };

    const handleDeleteFrame = (sceneId, shotId, frameType) => {
        const field = frameType === 'start' ? 'startFrameImage' : 'endFrameImage';
        handleShotUpdate(sceneId, shotId, field, null);
    };

    /**
     * Returns true when a shot has enough imagery to kick off video generation.
     *
     *   • Panel-based shot (any panels exist) → EVERY panel must have an image.
     *     Video generation uses references-to-video with the panel array; if
     *     any panel is missing we can't kick off without holes.
     *   • Legacy shot (no panels) → requires the legacy startFrameImage.
     */
    const hasGenerableVideoSource = (shot) => {
        const panels = getPanelsForShot(shot);
        if (Array.isArray(panels) && panels.length > 0) {
            return panels.every(p => !!p?.image);
        }
        return !!shot?.startFrameImage;
    };

    // ──────────────────────────────────────────────────────────────────────
    // PANEL IMAGES (Feb 2026, new projects only — `project.usesPanelImages`)
    //
    // Replaces the legacy Start Frame / End Frame dual-card layout with a
    // dynamic horizontal strip of N panel images, where N is the number of
    // panels parsed out of `shot.description` (see `utils/panelParser.js`).
    // Each panel image is stored at `shot.panels[i] = { id, image, prompt, ... }`.
    //
    // Phase 1 covers IMAGE generation only — video generation still consumes
    // the legacy start/end frames on old projects. Phase 2 will wire these
    // panel images as ordered references to the references-to-video pipeline.
    // ──────────────────────────────────────────────────────────────────────

    /**
     * Returns the panel array for a shot, falling back to parsing the shot's
     * `description` when `shot.panels` hasn't been seeded yet. Result is always
     * an array (possibly empty). Does NOT mutate state.
     */
    const getPanelsForShot = (shot) => {
        if (Array.isArray(shot?.panels) && shot.panels.length > 0) return shot.panels;
        const parsed = parsePanelsFromDescription(shot?.description || '');
        // Feb 13 2026 — diagnostic. If the description is substantial (looks
        // like a real shot brief) but the parser found no panel headers,
        // log the raw text so we can debug backend formatting drift.
        if (parsed.length === 0 && typeof shot?.description === 'string' && shot.description.length > 300) {
            console.warn(
                `[Storyboard] Shot "${shot.id}" description has ${shot.description.length} chars but 0 panels were parsed. LLM likely dropped the "Panel N (timecode):" headers. Raw description:`,
                shot.description,
            );
        }
        return parsed.map((p, i) => ({
            id: `panel-${shot?.id || 'x'}-${i}`,
            image: null,
            prompt: p.prompt,
            timecode: p.timecode,
            duration: p.duration,
            storyPurpose: p.storyPurpose,
            visualComposition: p.visualComposition,
            mainElements: p.mainElements,
            cameraMovement: p.cameraMovement,
            textOnScreen: p.textOnScreen,
            voiceOver: p.voiceOver,
        }));
    };

    /**
     * Writes a partial update (e.g. `{ image }`) into `shot.panels[panelIndex]`.
     * If `shot.panels` doesn't exist yet, seed it from `getPanelsForShot()`
     * first so we never write to a non-existent index.
     */
    const updatePanel = (sceneId, shotId, panelIndex, patch) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot  = scene?.shots.find(s => s.id === shotId);
        if (!shot) return;
        const current = Array.isArray(shot.panels) && shot.panels.length > 0
            ? shot.panels
            : getPanelsForShot(shot);
        const nextPanels = current.map((p, i) => i === panelIndex ? { ...p, ...patch } : p);
        handleShotUpdate(sceneId, shotId, 'panels', nextPanels);
    };

    const handleGeneratePanelImage = async (sceneId, shotId, panelIndex) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot  = scene?.shots.find(s => s.id === shotId);
        if (!shot) return;
        const panels = getPanelsForShot(shot);
        const panel  = panels[panelIndex];
        if (!panel) return;

        const prompt = panel.prompt;
        if (!prompt) {
            if (!silent) alert('This panel has no prompt yet — please add a shot description with panel structure first.');
            return;
        }

        const key = `${shotId}-panel-${panelIndex}`;
        setGeneratingFrames(prev => ({ ...prev, [key]: true }));

        try {
            // Use PER-PANEL cast/elements only. Panel chips reflect only the
            // panel's own content, so prompt sanitization (REMOVED / ADDED
            // clauses) must use the panel's set — not the shot-level set —
            // otherwise we'd inject "Also include <shot-level entity>" for
            // entities the panel doesn't actually feature.
            const chars = getCharactersForPanel(shot, panelIndex);
            const confirmedElements = getElementsForPanel(shot, panelIndex);
            const allEntities = [...chars, ...confirmedElements];
            const panelConfirmedIds = {
                characterIds: chars.map(c => c.id),
                elementIds:   confirmedElements.map(e => e.id),
            };

            const refs = [];
            if (panelIndex > 0 && panels[panelIndex - 1]?.image) {
                refs.push(panels[panelIndex - 1].image);
            }

            const hasReferenceImages = refs.length > 0 || allEntities.some(e => e.image || e.costumeAndAccessoryImages?.length);
            const modelId = projectData.modelPreferences?.image?.general?.[hasReferenceImages ? 'referenceToImage' : 'textToImage'];

            const result = await generateSceneImage(
                sanitizePromptForConfirmedEntities(prompt, shot, panelConfirmedIds),
                projectData.aspectRatio,
                allEntities,
                refs,
                modelId
            );
            const image = result.image || result;
            updatePanel(sceneId, shotId, panelIndex, { image });

            if (onTrackCreditsUsed && result.creditsUsed > 0) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }
            if (onSaveMedia) onSaveMedia([image]);
        } catch (err) {
            console.error('Panel image generation failed:', err);
            if (!silent) alert(`Failed to generate Panel ${panelIndex + 1} image: ${err.message}`);
        } finally {
            setGeneratingFrames(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleUploadPanelImage = (e, sceneId, shotId, panelIndex) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => updatePanel(sceneId, shotId, panelIndex, { image: reader.result });
        reader.readAsDataURL(file);
        if (e.target) e.target.value = '';
    };

    const handleDeletePanelImage = (sceneId, shotId, panelIndex) => {
        updatePanel(sceneId, shotId, panelIndex, { image: null });
    };

    const handleEditPanelPrompt = (sceneId, shotId, panelIndex, newPrompt) => {
        updatePanel(sceneId, shotId, panelIndex, { prompt: newPrompt });
    };

    // ── Per-panel cast & element selection ──────────────────────────────
    // Each panel may have its own characterIds / elementIds. When a panel has
    // no per-panel selection yet, generation falls back to the shot-level
    // shot.characterIds / shot.elementIds so existing data keeps working.

    const handleAddCharacterToPanel = (sceneId, shotId, panelIndex, characterId) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot  = scene?.shots.find(s => s.id === shotId);
        if (!shot) return;
        const panels = Array.isArray(shot.panels) && shot.panels.length > 0 ? shot.panels : getPanelsForShot(shot);
        const current = panels[panelIndex]?.characterIds || [];
        if (current.includes(characterId)) return;
        updatePanel(sceneId, shotId, panelIndex, { characterIds: [...current, characterId] });
    };

    const handleRemoveCharacterFromPanel = (sceneId, shotId, panelIndex, characterId) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot  = scene?.shots.find(s => s.id === shotId);
        if (!shot) return;
        const panels = Array.isArray(shot.panels) && shot.panels.length > 0 ? shot.panels : getPanelsForShot(shot);
        const panel = panels[panelIndex];
        // Seed-on-first-remove: explicit override → auto-detected. No shot-level
        // fallback — panel chips reflect only the panel's content.
        let current = panel?.characterIds || [];
        if (current.length === 0) {
            const detected = detectEntityIdsInPanel(panel, projectData.characters || []);
            current = detected.characterIds;
        }
        updatePanel(sceneId, shotId, panelIndex, { characterIds: current.filter(id => id !== characterId) });
    };

    const handleAddElementToPanel = (sceneId, shotId, panelIndex, elementId) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot  = scene?.shots.find(s => s.id === shotId);
        if (!shot) return;
        const panels = Array.isArray(shot.panels) && shot.panels.length > 0 ? shot.panels : getPanelsForShot(shot);
        const current = panels[panelIndex]?.elementIds || [];
        if (current.includes(elementId)) return;
        updatePanel(sceneId, shotId, panelIndex, { elementIds: [...current, elementId] });
    };

    const handleRemoveElementFromPanel = (sceneId, shotId, panelIndex, elementId) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot  = scene?.shots.find(s => s.id === shotId);
        if (!shot) return;
        const panels = Array.isArray(shot.panels) && shot.panels.length > 0 ? shot.panels : getPanelsForShot(shot);
        const panel = panels[panelIndex];
        // Seed-on-first-remove: explicit override → auto-detected. No shot-level
        // fallback — panel chips reflect only the panel's content.
        let current = panel?.elementIds || [];
        if (current.length === 0) {
            const detected = detectEntityIdsInPanel(panel, projectData.characters || []);
            current = detected.elementIds;
        }
        updatePanel(sceneId, shotId, panelIndex, { elementIds: current.filter(id => id !== elementId) });
    };

    /** Returns the characters confirmed for a specific panel.
     * Priority:
     *   1. Explicit panel-level override (`panel.characterIds`) — set when the
     *      user clicks "+ Add" / removes a chip on this panel.
     *   2. Auto-detected from the panel's natural-language description
     *      (`detectEntityIdsInPanel`).
     *   3. Empty — panels are self-contained, so we do NOT fall back to
     *      shot-level characters. (A panel that doesn't mention a character
     *      should not show that character's chip.) */
    const getCharactersForPanel = (shot, panelIndex) => {
        const panels = Array.isArray(shot?.panels) && shot.panels.length > 0 ? shot.panels : getPanelsForShot(shot);
        const panel = panels[panelIndex];
        const projectChars = projectData.characters || [];
        let ids;
        if (Array.isArray(panel?.characterIds) && panel.characterIds.length > 0) {
            ids = panel.characterIds;
        } else {
            const detected = detectEntityIdsInPanel(panel, projectChars);
            ids = detected.characterIds;
        }
        return projectChars.filter(c => (c.category ?? 'character') === 'character' && ids.includes(c.id));
    };

    /** Returns the elements confirmed for a specific panel, with the same
     * (1) explicit-override → (2) auto-detect → (3) empty priority. No
     * shot-level fallback — panel chips reflect only the panel's content. */
    const getElementsForPanel = (shot, panelIndex) => {
        const panels = Array.isArray(shot?.panels) && shot.panels.length > 0 ? shot.panels : getPanelsForShot(shot);
        const panel = panels[panelIndex];
        const projectChars = projectData.characters || [];
        let ids;
        if (Array.isArray(panel?.elementIds) && panel.elementIds.length > 0) {
            ids = panel.elementIds;
        } else {
            const detected = detectEntityIdsInPanel(panel, projectChars);
            ids = detected.elementIds;
        }
        return projectChars.filter(c => c.category === 'element' && ids.includes(c.id));
    };

    const handleVideoFileChange = (e, shotId) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const sceneId = scenes.find(s => s.shots.some(shot => shot.id === shotId))?.id;
            if (sceneId) {
                handleShotUpdate(sceneId, shotId, 'video', reader.result);
            }
        };
        reader.readAsDataURL(file);

        if (e.target) e.target.value = '';
    };

    const handleSaveFromImageEditor = (imageData) => {
        if (editingInfo) {
            const { shot, frame, panelIndex } = editingInfo;
            const sceneId = scenes.find(s => s.shots.some(sh => sh.id === shot.id))?.id;
            if (sceneId) {
                if (typeof panelIndex === 'number') {
                    // New panel-image flow: write image to shot.panels[panelIndex].image
                    updatePanel(sceneId, shot.id, panelIndex, { image: imageData });
                } else {
                    // Legacy start/end frame flow
                    const field = frame === 'start' ? 'startFrameImage' : 'endFrameImage';
                    handleShotUpdate(sceneId, shot.id, field, imageData);
                }
            }
            setEditingInfo(null);
        }
    };

    const handleSaveFromPreProduction = (shotId, updates) => {
        const sceneId = scenes.find(s => s.shots.some(shot => shot.id === shotId))?.id;
        if (!sceneId) {
            setPreppingVideoForShot(null);
            return;
        }
        // Persist all relevant fields the modal may save — not just the video.
        // This ensures dialogue edits made inside the modal flow back to the
        // shot, so future batch generations (Generate All Scene/Project Videos)
        // use the user's edited text. Previously only video+duration were
        // saved, which silently dropped dialogue edits.
        const shotUpdates = {};
        if (updates.videoUrl) {
            shotUpdates.video = updates.videoUrl;
            console.log(`📹 Saved video for shot ${shotId}:`, updates.videoUrl?.substring(0, 60) + '...', 'duration:', updates.videoDuration || 'N/A');
        }
        if (updates.videoDuration) shotUpdates.videoDuration = updates.videoDuration;
        if (Array.isArray(updates.dialogues)) shotUpdates.dialogues = updates.dialogues;
        if (Array.isArray(updates.textOverlays)) shotUpdates.textOverlays = updates.textOverlays;
        if (Array.isArray(updates.dialogueAudios)) shotUpdates.dialogueAudios = updates.dialogueAudios;
        if (updates.narrationAudio !== undefined) shotUpdates.narrationAudio = updates.narrationAudio;

        if (Object.keys(shotUpdates).length > 0) {
            handleShotUpdateMultiple(sceneId, shotId, shotUpdates);
            // Stage 9: auto-save after single-shot pre-production save (300ms delay)
            if (onAutoSave) setTimeout(() => onAutoSave({ reason: `shot-save:${shotId}` }), 300);
        }
        setPreppingVideoForShot(null);
    };

    const handleSaveFromAnimation = (shotId, videoUrl) => {
        const sceneId = scenes.find(s => s.shots.some(shot => shot.id === shotId))?.id;
        if (sceneId) {
            handleShotUpdate(sceneId, shotId, 'video', videoUrl);
        }
        setAnimatingShot(null);
    };

    const handleTranscribeAndSaveDescription = async (shotId, audio) => {
        setManagingAudioForShot(null);
        
        try {
            const newDescription = await transcribeShotNarration(audio.data);
            const sceneId = scenes.find(s => s.shots.some(shot => shot.id === shotId))?.id;
            if (sceneId) {
                handleShotUpdate(sceneId, shotId, 'description', newDescription);
            }
        } catch (err) {
            console.error("Transcription failed:", err);
            alert(`Transcription failed: ${err.message}`);
        }
    };

    const handleSubmit = () => {
        onSubmit(scenes);
    };

    /**
     * Smart character matching for a shot
     * Looks at shot description, start frame prompt, and end frame prompt to find matching characters
     */
    /**
     * Pure lookup: returns character objects for the shot's confirmed
     * `characterIds`. No text scanning, no fuzzy matching. The chip row in
     * the UI is the single source of truth. If a character is expected to
     * appear visually in the shot but isn't in `characterIds`, the user must
     * add them via the "+ Add" chip.
     *
     * Reconciliation (auto-matching character names from description/frame
     * prompts) runs ONCE at storyboard creation / pre-production time via
     * `reconcileShotEntities` and persists the result to `characterIds`.
     * It is NOT re-run here to avoid silently undoing user edits.
     */
    const getCharactersForShot = (shot) => {
        const allCharacters = projectData.characters?.filter(c =>
            (c.category ?? 'character') === 'character' || c.category === 'narrator'
        ) || [];
        const ids = Array.isArray(shot.characterIds) ? shot.characterIds : [];
        const matched = allCharacters.filter(c => ids.includes(c.id));
        return matched.map(char => ({
            ...char,
            costumeAndAccessoryImages: char.costumes?.length > 0 ? char.costumes : (char.costumeAndAccessoryImages || []),
        }));
    };

    /**
     * Pure lookup: returns element objects for the shot's confirmed
     * `elementIds`. No text scanning. See `getCharactersForShot` for rationale.
     */
    const getElementsForShot = (shot) => {
        const allElements = projectData.characters?.filter(c => c.category === 'element') || [];
        const ids = Array.isArray(shot.elementIds) ? shot.elementIds : [];
        return allElements.filter(el => ids.includes(el.id));
    };

    // ---- One-time reconciliation: auto-match character/element names from
    // shot description + frame prompts, seed `characterIds` / `elementIds`.
    //
    // Behaviour:
    //   • Runs ONCE per shot on mount (or when a new shot appears without a
    //     lock flag). After the first run, `shot.entitiesLocked = true` and
    //     future edits to the chip row will NEVER be overridden by this scan.
    //   • Uses word-boundary regex (\b) so "Jay" doesn't match "banjaya" and
    //     "Pat" doesn't match "pattern". Eliminates the false-positive
    //     pollution that made Nova/Luna/Bolt appear in Zipp-only shots.
    //   • Excludes the narrator from visual-reference scanning — narrators
    //     speak (via dialogue payload) but are not visual references unless
    //     the user explicitly adds them to `characterIds`.
    //   • Preserves anything already in characterIds/elementIds — union only.
    const reconcileRanRef = useRef(new Set());
    useEffect(() => {
        if (!projectData.characters || scenes.length === 0) return;
        const allCharacters = projectData.characters.filter(c =>
            (c.category ?? 'character') === 'character' && c.category !== 'narrator'
        );
        const allElements = projectData.characters.filter(c => c.category === 'element');
        if (allCharacters.length === 0 && allElements.length === 0) return;

        const buildRegex = (name) => {
            if (!name) return null;
            // Escape regex specials, word-boundary flanks, case-insensitive.
            const esc = name.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            if (!esc) return null;
            return new RegExp(`\\b${esc}\\b`, 'i');
        };

        const matchesInText = (entityName, text) => {
            if (!entityName || !text) return false;
            const full = buildRegex(entityName);
            if (full && full.test(text)) return true;
            // First-name fallback ONLY when the first token is ≥4 chars and
            // is distinct from common words. Applied with word boundaries.
            const first = entityName.trim().split(/\s+/)[0];
            if (first && first.length >= 4 && first.toLowerCase() !== entityName.toLowerCase()) {
                const re = buildRegex(first);
                return !!(re && re.test(text));
            }
            return false;
        };

        let changed = false;
        const updatedScenes = scenes.map(scene => ({
            ...scene,
            shots: scene.shots.map(shot => {
                // Skip shots the user has already curated OR that we've
                // already reconciled in this mount (avoid double-writes).
                if (shot.entitiesLocked) return shot;
                if (reconcileRanRef.current.has(shot.id)) return shot;

                const searchText = [
                    shot.description || '',
                    shot.startFramePrompt || '',
                    shot.endFramePrompt || '',
                    shot.title || ''
                ].join(' ');

                // Characters (word-boundary, narrator excluded)
                const currentCharIds = Array.isArray(shot.characterIds) ? shot.characterIds : [];
                const extraCharIds = allCharacters
                    .filter(c => !currentCharIds.includes(c.id) && matchesInText(c.name, searchText))
                    .map(c => c.id);

                // Elements — same word-boundary approach (no fuzzy stemming)
                const currentElemIds = Array.isArray(shot.elementIds) ? shot.elementIds : [];
                const extraElemIds = allElements
                    .filter(e => !currentElemIds.includes(e.id) && matchesInText(e.name, searchText))
                    .map(e => e.id);

                if (extraCharIds.length === 0 && extraElemIds.length === 0) {
                    // Still mark as reconciled-this-mount and lock so future
                    // mounts don't re-scan unnecessarily.
                    reconcileRanRef.current.add(shot.id);
                    changed = true;
                    return { ...shot, entitiesLocked: true };
                }

                changed = true;
                reconcileRanRef.current.add(shot.id);
                if (extraCharIds.length > 0) console.log(`🧩 Reconcile shot "${shot.title || shot.id}": +chars`, extraCharIds);
                if (extraElemIds.length > 0) console.log(`🧩 Reconcile shot "${shot.title || shot.id}": +elems`, extraElemIds);
                return {
                    ...shot,
                    characterIds: [...currentCharIds, ...extraCharIds],
                    elementIds: [...currentElemIds, ...extraElemIds],
                    entitiesLocked: true
                };
            })
        }));

        if (changed) syncToParent(updatedScenes);
    // Intentionally depend only on scenes.length + characters — we re-run when
    // new shots/characters appear, but skip already-locked shots inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scenes.length, projectData.characters?.length]);


    // Single-attempt pre-production call (no UI status management)
    const attemptScenePreProduction = async (scene) => {
        const allCharacters = projectData.characters || [];
        const allNonElementChars = allCharacters.filter(c => c.category !== 'element');
        const allElements = allCharacters.filter(c => c.category === 'element');

        // Pin a single project-wide narrator. Admin now requires this on
        // every preproduction call so the SAME narrator voice is used across
        // every clip. Without it, admin falls back to "first character with
        // category='narrator'", which is fragile if the characters array
        // changes mid-project. We resolve it deterministically from project
        // state (first narrator entry), so every scene's call carries the
        // same narrator_id.
        const audioStyle = projectData.audioStyle || 'both';
        const needsNarrator = audioStyle === 'narration' || audioStyle === 'both';
        const projectNarrator = needsNarrator
            ? allCharacters.find(c => c.category === 'narrator')
            : null;

        const metadata = {
            aspectRatio: projectData.aspectRatio || '16:9',
            language: projectData.language || 'English',
            audio_style: audioStyle,
            cast_mode: projectData.castMode || 'with_actors',
            genre: projectData.genre || '',
            projectId: projectData.projectId || projectData.id,
        };
        if (projectNarrator) {
            metadata.narrator_id = projectNarrator.id;
            metadata.project_narrator = {
                id: projectNarrator.id,
                name: projectNarrator.name,
                voiceId: projectNarrator.voiceId,
            };
        }

        const latestScene = scenesRef.current.find(s => s.id === scene.id) || scene;

        // ---- SCENE-SCOPED PAYLOAD FILTERING (prevents admin-backend LLM timeout) ----
        const sceneCharIds = new Set();
        for (const sh of latestScene.shots || []) {
            for (const c of sh.characters || []) {
                sceneCharIds.add(typeof c === 'string' ? c : c?.id);
            }
            for (const d of sh.dialogues || []) {
                if (d.characterId) sceneCharIds.add(d.characterId);
            }
        }
        const relevantCharacters = allNonElementChars.filter(c =>
            c.category === 'narrator' || sceneCharIds.has(c.id)
        );

        const haystack = [
            latestScene.description || '',
            latestScene.title || '',
            latestScene.location || '',
            ...(latestScene.shots || []).flatMap(s => [
                s.description || '',
                s.startFramePrompt || '',
                s.endFramePrompt || ''
            ])
        ].join(' ').toLowerCase();

        const relevantElements = allElements.filter(e => {
            const name = (e.name || '').toLowerCase();
            const id = (e.id || '').toLowerCase();
            if (!name && !id) return false;
            if (name && haystack.includes(name)) return true;
            if (id && haystack.includes(id)) return true;
            const tokens = name.split(/\s+/).filter(t => t.length > 3);
            return tokens.some(t => haystack.includes(t));
        });

        const compactScript = (projectData.script || '').slice(0, 3000);

        console.log(
            `📦 Scene ${scene.id} payload: ` +
            `${relevantCharacters.length}/${allNonElementChars.length} chars, ` +
            `${relevantElements.length}/${allElements.length} elements, ` +
            `script=${compactScript.length}ch`
        );

        const scenePayload = {
            id: latestScene.id,
            title: latestScene.title || `Scene ${scenesRef.current.indexOf(latestScene) + 1}`,
            description: latestScene.description || '',
            shots: latestScene.shots.map(sh => ({
                id: sh.id,
                description: sh.description || '',
                startFramePrompt: sh.startFramePrompt || '',
                endFramePrompt: sh.endFramePrompt || '',
                cinematography: sh.cinematography || {},
                characters: sh.characters || [],
                dialogues: sh.dialogues || []
            }))
        };

        return await generatePreProduction(
            scenePayload, compactScript, relevantCharacters, relevantElements, metadata
        );
    };

    // Reusable per-scene pre-production runner with auto-retry + exponential backoff
    const runScenePreProduction = async (scene) => {
        const MAX_ATTEMPTS = 3;
        // Backoff delays BEFORE attempts 2 and 3 (attempt 1 is immediate)
        const BACKOFF_MS = [0, 10000, 30000];

        setPreProductionStatus(prev => ({ ...prev, [scene.id]: 'loading' }));

        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            if (attempt > 1) {
                setPreProductionStatus(prev => ({
                    ...prev,
                    [scene.id]: { state: 'retrying', attempt, maxAttempts: MAX_ATTEMPTS }
                }));
                console.log(`⏱️ Scene ${scene.id}: waiting ${BACKOFF_MS[attempt - 1] / 1000}s before retry ${attempt}/${MAX_ATTEMPTS}...`);
                await new Promise(r => setTimeout(r, BACKOFF_MS[attempt - 1]));
                setPreProductionStatus(prev => ({ ...prev, [scene.id]: 'loading' }));
            }

            try {
                const result = await attemptScenePreProduction(scene);

                if (result.success && result.shots?.length > 0) {
                    // Stamp selectedVideoModel on each pre-production-enriched
                    // shot BEFORE we commit to parent state. This guarantees
                    // both batch and modal find the resolved model id in
                    // shot.preProduction.selectedVideoModel and can skip the
                    // per-shot `getAllVideoModels()` fetch entirely.
                    const latestShots = scenesRef.current.find(s => s.id === scene.id)?.shots || [];
                    const shotsForStamp = result.shots.map(rs => {
                        const existing = latestShots.find(sh => sh.id === rs.id);
                        return {
                            id: rs.id,
                            endFrameImage: existing?.endFrameImage,
                            preProduction: rs.preProduction
                        };
                    });
                    const stampedShots = await stampSelectedVideoModelOnShots(shotsForStamp);
                    const stampedById = new Map(stampedShots.map(s => [s.id, s]));

                    const latest = scenesRef.current;
                    const updatedScenes = latest.map(s => {
                        if (s.id !== scene.id) return s;
                        return {
                            ...s,
                            shots: s.shots.map(sh => {
                                const stamped = stampedById.get(sh.id);
                                if (stamped?.preProduction) {
                                    return { ...sh, preProduction: stamped.preProduction };
                                }
                                return sh;
                            })
                        };
                    });
                    syncToParent(updatedScenes);
                    setPreProductionStatus(prev => ({ ...prev, [scene.id]: 'ready' }));
                    console.log(`✅ Pre-production ready for Scene ${scene.id} (attempt ${attempt}/${MAX_ATTEMPTS})`);
                    // Stage 3: auto-save after pre-production is ready for this scene.
                    // Pass projectOverride with fresh scenes so autosave doesn't race
                    // React's async setState chain (syncToParent → setActiveProject
                    // → activeProjectRef update).
                    if (onAutoSave) {
                        const override = { ...projectData, scenes: updatedScenes, lastModified: Date.now() };
                        setTimeout(() => onAutoSave({ reason: `pre-prod-ready:${scene.id}`, projectOverride: override }), 0);
                    }
                    return true;
                }

                // Soft failure — empty shots
                console.warn(`⚠️ Pre-production returned no data for Scene ${scene.id} (attempt ${attempt}/${MAX_ATTEMPTS})`);
                if (attempt === MAX_ATTEMPTS) {
                    setPreProductionStatus(prev => ({ ...prev, [scene.id]: 'failed' }));
                    return false;
                }
            } catch (err) {
                console.error(`❌ Pre-production attempt ${attempt}/${MAX_ATTEMPTS} failed for Scene ${scene.id}:`, err.message);
                if (attempt === MAX_ATTEMPTS) {
                    setPreProductionStatus(prev => ({ ...prev, [scene.id]: 'failed' }));
                    return false;
                }
            }
        }
        return false;
    };

    // Manual retry for a single scene's pre-production (user-initiated from status pill)
    const handleRetryPreProduction = async (sceneId) => {
        const scene = scenesRef.current.find(s => s.id === sceneId);
        if (!scene) return;
        // Clear existing preProduction data on all shots so retry regenerates fresh
        const updatedScenes = scenesRef.current.map(s => {
            if (s.id !== sceneId) return s;
            return { ...s, shots: s.shots.map(sh => ({ ...sh, preProduction: undefined })) };
        });
        syncToParent(updatedScenes);
        await runScenePreProduction(scene);
    };

    // Auto-trigger pre-production for all scenes after storyboard loads
    const preProductionRanRef = useRef(false);
    useEffect(() => {
        if (preProductionRanRef.current || scenes.length === 0) return;
        
        // Check if any scene already has preProduction data
        const allHavePreProd = scenes.every(s => s.shots.every(sh => sh.preProduction));
        if (allHavePreProd) {
            const status = {};
            scenes.forEach(s => { status[s.id] = 'ready'; });
            setPreProductionStatus(status);
            preProductionRanRef.current = true;
            return;
        }

        preProductionRanRef.current = true;
        
        // Launch pre-production for each scene with bounded concurrency (backend-friendly)
        const runAll = async () => {
            const initialStatus = {};
            scenes.forEach(s => {
                const hasPreProd = s.shots.every(sh => sh.preProduction);
                initialStatus[s.id] = hasPreProd ? 'ready' : 'loading';
            });
            setPreProductionStatus(initialStatus);

            const queue = scenes.filter(s => !s.shots.every(sh => sh.preProduction));
            const CONCURRENCY = 3;

            const worker = async () => {
                while (queue.length > 0) {
                    const scene = queue.shift();
                    if (!scene) break;
                    await runScenePreProduction(scene);
                }
            };
            await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => worker()));
        };

        runAll();
    }, [scenes.length]); // eslint-disable-line react-hooks/exhaustive-deps



    const getSceneImageModelId = (hasReferences) => {
        const generalPrefs = projectData.modelPreferences?.image?.general;
        const pref = hasReferences ? generalPrefs?.referenceToImage : generalPrefs?.textToImage;
        return pref;
    };

    /**
     * Parallel panel-image generation for an entire scene.
     *
     * Used when `projectData.usesPanelImages` is true (new panel-based shots).
     * Every panel that has a prompt but no image is generated SIMULTANEOUSLY
     * via `Promise.allSettled`. Concurrent writes are made race-safe by
     * collecting every successful result first, then applying them in a
     * SINGLE `syncToParent(...)` mutation — no two completions can clobber
     * each other's panel updates.
     *
     * Trade-off vs the legacy sequential start/end-frame flow:
     *   • No inter-panel continuity reference (panel N-1's image hasn't been
     *     generated when panel N starts). If you want strict continuity,
     *     generate panel 1 individually first, then click "Gen Images".
     */
    const handleGenerateScenePanelImages = async (sceneId) => {
        const targetScene = scenesRef.current.find(s => s.id === sceneId);
        if (!targetScene) return;

        // Build the task list — only panels that have a prompt AND no image yet
        const tasks = [];
        for (const shot of targetScene.shots) {
            const panels = Array.isArray(shot.panels) && shot.panels.length > 0
                ? shot.panels
                : getPanelsForShot(shot);
            for (let panelIndex = 0; panelIndex < panels.length; panelIndex++) {
                const panel = panels[panelIndex];
                if (panel?.image) continue;
                if (!panel?.prompt) continue;
                tasks.push({ shot, panel, panelIndex });
            }
        }

        if (tasks.length === 0) {
            if (!silent) alert('All panels in this scene already have images.');
            return;
        }

        const totalCost = tasks.length * CREDIT_COSTS.IMAGE_GENERATION;
        const sceneNumForMsg = scenes.findIndex(s => s.id === sceneId) + 1;
        if (!silent && !confirm(`Generate ${tasks.length} panel image${tasks.length > 1 ? 's' : ''} for Scene ${sceneNumForMsg}? (Est. ${totalCost} credits)\n\nAll panels generate in parallel.`)) {
            return;
        }

        setGeneratingScenes(prev => new Set(prev).add(sceneId));
        setSceneErrors(prev => ({ ...prev, [sceneId]: null }));
        // Per-panel spinners
        setGeneratingFrames(prev => {
            const next = { ...prev };
            tasks.forEach(t => { next[`${t.shot.id}-panel-${t.panelIndex}`] = true; });
            return next;
        });

        let totalCreditsUsed = 0;
        let lastCreditsRemaining = null;
        const generatedJobIds = [];
        const generatedImages = [];

        try {
            await preloadProjectAssets(projectData);

            // Encapsulate one panel's generation in a closure so we can re-run
            // it during retry passes for any panel that didn't get an image.
            const runPanelTask = async (t) => {
                const { shot, panel, panelIndex } = t;
                const chars = getCharactersForPanel(shot, panelIndex);
                const confirmedElements = getElementsForPanel(shot, panelIndex);
                const allEntities = [...chars, ...confirmedElements];
                const panelConfirmedIds = {
                    characterIds: chars.map(c => c.id),
                    elementIds:   confirmedElements.map(e => e.id),
                };
                const hasReferenceImages = allEntities.some(e => e.image || e.costumeAndAccessoryImages?.length);
                const modelId = projectData.modelPreferences?.image?.general?.[hasReferenceImages ? 'referenceToImage' : 'textToImage'];

                const result = await generateSceneImage(
                    sanitizePromptForConfirmedEntities(panel.prompt, shot, panelConfirmedIds),
                    projectData.aspectRatio,
                    allEntities,
                    [], // No inter-panel ref — parallel generation
                    modelId
                );
                return {
                    shotId: shot.id,
                    panelIndex,
                    image: result.image || result,
                    jobId: result.jobId || null,
                    creditsUsed: result.creditsUsed || 0,
                    creditsRemaining: result.creditsRemaining,
                };
            };

            // Up to 3 attempts total: initial parallel pass + 2 retry passes.
            // Each pass: fire pending tasks in parallel → aggregate → batch-write
            // successful results → free `rePollJob` recovery for jobs that may
            // still complete in the background → carry forward truly-failed
            // panels to the next pass.
            const MAX_RETRY_PASSES = 2;
            let pendingTasks = tasks;
            let allFailed = [];

            for (let attempt = 0; attempt <= MAX_RETRY_PASSES; attempt++) {
                if (pendingTasks.length === 0) break;
                if (attempt > 0) {
                    console.log(`🔄 Retry pass ${attempt}/${MAX_RETRY_PASSES} for ${pendingTasks.length} failed panel(s)`);
                }

                // Fire pending tasks in parallel.
                const results = await Promise.allSettled(pendingTasks.map(runPanelTask));

                const fulfilled = [];
                const failed = [];
                results.forEach((r, idx) => {
                    const t = pendingTasks[idx];
                    if (r.status === 'fulfilled' && r.value?.image) {
                        fulfilled.push(r.value);
                        totalCreditsUsed += r.value.creditsUsed;
                        if (r.value.creditsRemaining !== undefined) lastCreditsRemaining = r.value.creditsRemaining;
                        if (r.value.jobId) generatedJobIds.push(r.value.jobId);
                        generatedImages.push(r.value.image);
                    } else {
                        const errMsg = r.reason?.message || 'unknown error';
                        const jobId = r.reason?.jobId || null;
                        console.warn(`⚠️ [pass ${attempt}] Panel ${t.panelIndex} (shot ${t.shot.id}) failed: ${errMsg}`);
                        failed.push({ task: t, shotId: t.shot.id, panelIndex: t.panelIndex, jobId });
                    }
                });

                // ATOMIC batch update — apply ALL successful panel images in a
                // single `syncToParent` so concurrent completions cannot clobber
                // each other's writes.
                if (fulfilled.length > 0) {
                    const latest = scenesRef.current;
                    const newScenes = latest.map(scene => {
                        if (scene.id !== sceneId) return scene;
                        return {
                            ...scene,
                            shots: scene.shots.map(shot => {
                                const updates = fulfilled.filter(f => f.shotId === shot.id);
                                if (updates.length === 0) return shot;
                                const current = Array.isArray(shot.panels) && shot.panels.length > 0
                                    ? shot.panels
                                    : getPanelsForShot(shot);
                                const nextPanels = current.map((p, i) => {
                                    const u = updates.find(u => u.panelIndex === i);
                                    return u ? { ...p, image: u.image } : p;
                                });
                                return { ...shot, panels: nextPanels };
                            })
                        };
                    });
                    syncToParent(newScenes);
                }

                // Free `rePollJob` recovery (no credits) for panels that
                // failed with a jobId — backend may still have produced the
                // image by now. Successful recoveries are removed from
                // `failed` so we don't burn fresh credits on the next pass.
                const recovered = new Set();
                for (const ff of failed) {
                    if (!ff.jobId) continue;
                    try {
                        const rePollResult = await rePollJob(ff.jobId, 15, 4000);
                        if (rePollResult.success && rePollResult.imageUrl) {
                            const latest2 = scenesRef.current;
                            const merged = latest2.map(scene => {
                                if (scene.id !== sceneId) return scene;
                                return {
                                    ...scene,
                                    shots: scene.shots.map(shot => {
                                        if (shot.id !== ff.shotId) return shot;
                                        const current = Array.isArray(shot.panels) && shot.panels.length > 0
                                            ? shot.panels
                                            : getPanelsForShot(shot);
                                        const nextPanels = current.map((p, i) =>
                                            i === ff.panelIndex ? { ...p, image: rePollResult.imageUrl } : p);
                                        return { ...shot, panels: nextPanels };
                                    })
                                };
                            });
                            syncToParent(merged);
                            generatedImages.push(rePollResult.imageUrl);
                            recovered.add(`${ff.shotId}::${ff.panelIndex}`);
                            console.log(`✅ Recovered panel ${ff.panelIndex} for shot ${ff.shotId} via rePollJob`);
                        }
                    } catch (e) {
                        console.warn(`❌ rePollJob failed for panel ${ff.panelIndex}: ${e.message}`);
                    }
                }

                // Carry forward only the panels that still don't have an image.
                pendingTasks = failed
                    .filter(ff => !recovered.has(`${ff.shotId}::${ff.panelIndex}`))
                    .map(ff => ff.task);
                allFailed = pendingTasks;
            }

            if (onSaveMedia && generatedImages.length > 0) {
                onSaveMedia(generatedImages);
            }
            if (onTrackCreditsUsed && totalCreditsUsed > 0) {
                onTrackCreditsUsed(totalCreditsUsed, lastCreditsRemaining,
                    generatedJobIds[generatedJobIds.length - 1] || null);
            }

            if (allFailed.length > 0 && !silent) {
                setSceneErrors(prev => ({
                    ...prev,
                    [sceneId]: `${allFailed.length} of ${tasks.length} panel${tasks.length > 1 ? 's' : ''} failed after ${MAX_RETRY_PASSES + 1} attempts. Try regenerating individual panels.`
                }));
            } else if (allFailed.length === 0 && tasks.length > 0) {
                console.log(`✅ All ${tasks.length} panel image${tasks.length > 1 ? 's' : ''} generated for scene ${sceneId}`);
            }
        } catch (err) {
            console.error('Parallel panel image generation crashed:', err);
            setSceneErrors(prev => ({ ...prev, [sceneId]: err.message || 'Unknown error during parallel panel generation.' }));
        } finally {
            setGeneratingScenes(prev => {
                const newSet = new Set(prev);
                newSet.delete(sceneId);
                return newSet;
            });
            // Always clear ALL the per-panel "generating" flags we set,
            // even on partial failure (the panels that failed will simply
            // remain without an image so the user can retry them).
            setGeneratingFrames(prev => {
                const next = { ...prev };
                tasks.forEach(t => { next[`${t.shot.id}-panel-${t.panelIndex}`] = false; });
                return next;
            });
            if (onAutoSave) setTimeout(() => onAutoSave({ reason: `scene-panels:${sceneId}` }), 300);
        }
    };

    const handleGenerateSceneImages = async (sceneId) => {
        // Panel-based projects use the dedicated parallel generator.
        if (projectData?.usesPanelImages) {
            return handleGenerateScenePanelImages(sceneId);
        }
        const targetScene = scenes.find(s => s.id === sceneId);
        if (!targetScene) return;

        // Calculate frames to generate with shot continuity in mind:
        // - Shot 1: May need both start and end frames
        // - Shot 2+: Start frame is REUSED from previous shot's end frame, only end frame needs generation
        let framesToGenerate = 0;
        for (let i = 0; i < targetScene.shots.length; i++) {
            const shot = targetScene.shots[i];
            
            if (i === 0) {
                // First shot: may need start frame
                if (!shot.startFrameImage) framesToGenerate++;
            }
            // For shots 2+: start frame will be reused from previous end frame (no generation needed)
            
            // All shots: may need end frame
            if (!shot.endFrameImage) framesToGenerate++;
        }
        
        if (framesToGenerate === 0) {
            if (!silent) alert('All frames in this scene already have images.');
            return;
        }
        
        const totalCost = framesToGenerate * CREDIT_COSTS.IMAGE_GENERATION;
        if (!silent && !confirm(`Generate ${framesToGenerate} frame${framesToGenerate > 1 ? 's' : ''} for Scene ${scenes.indexOf(targetScene) + 1}? (Est. ${totalCost} credits)\n\nNote: Shot continuity is enabled - end frames will be reused as start frames for subsequent shots.`)) return;
        // Credits are deducted by backend - just proceed with generation

        setGeneratingScenes(prev => new Set(prev).add(sceneId));
        setSceneErrors(prev => ({ ...prev, [sceneId]: null }));

        const generatedImages = [];
        let totalCreditsUsed = 0;
        let lastCreditsRemaining = null;
        const generatedJobIds = [];

        // Track failed frames for re-polling at end (don't re-generate, just re-fetch)
        const failedFrames = []; // { shotId, frameType, jobId, prompt, entities, refs, modelId }

        try {
            await preloadProjectAssets(projectData);
            let previousEndFrame = undefined;
            // Always read the latest scene state from ref (not stale closure)
            let targetSceneState = { ...scenesRef.current.find(s => s.id === sceneId) };
            if (!targetSceneState) return;

            for (let i = 0; i < targetScene.shots.length; i++) {
                const shot = targetScene.shots[i];
                const shotIndex = i; // 0-based index
                const charactersInShot = getCharactersForShot(shot);
                // Only use CONFIRMED elements (explicitly added via elementIds) for generation
                const allElements = projectData.characters?.filter(c => c.category === 'element') || [];
                const elementsInShot = allElements.filter(el => shot.elementIds?.includes(el.id));
                const allEntities = [...charactersInShot, ...elementsInShot];
                
                let startFrameImage;
                
                // SHOT CONTINUITY LOGIC:
                // For shot 1: Generate fresh start frame
                // For shot 2+: ALWAYS use previous shot's end frame as start frame
                if (shotIndex === 0) {
                    // First shot: Use existing start frame or generate new one
                    startFrameImage = shot.startFrameImage;
                    
                    if (!startFrameImage && shot.startFramePrompt) {
                        const hasInputImages = allEntities.some(c => c.image);
                        const modelId = getSceneImageModelId(hasInputImages);
                        
                        console.log(`🎬 Scene ${scenes.findIndex(s => s.id === sceneId) + 1}, Shot 1: Generating start frame`);
                        try {
                            const result = await generateSceneImage(sanitizePromptForConfirmedEntities(shot.startFramePrompt, shot), projectData.aspectRatio, allEntities, [], modelId);
                            startFrameImage = result.image || result;
                            if (result.creditsUsed) totalCreditsUsed += result.creditsUsed;
                            if (result.creditsRemaining !== undefined) lastCreditsRemaining = result.creditsRemaining;
                            if (result.jobId) generatedJobIds.push(result.jobId);
                            if (startFrameImage) generatedImages.push(startFrameImage);
                        } catch (err) {
                            console.warn(`⚠️ Start frame failed:`, err.message);
                            failedFrames.push({ shotId: shot.id, frameType: 'start', sceneId, jobId: err.jobId || null });
                        }
                    }
                } else {
                    // Subsequent shots: ALWAYS use previous end frame for continuity
                    if (previousEndFrame) {
                        console.log(`🎬 Scene ${scenes.findIndex(s => s.id === sceneId) + 1}, Shot ${shotIndex + 1}: REUSING previous end frame as start frame (continuity)`);
                        startFrameImage = previousEndFrame;
                    } else {
                        // Fallback: If no previous end frame (shouldn't happen normally), use existing or generate
                        console.warn(`⚠️ Shot ${shotIndex + 1}: No previous end frame available for continuity, generating fresh`);
                        startFrameImage = shot.startFrameImage;
                        
                        if (!startFrameImage && shot.startFramePrompt) {
                            const hasInputImages = allEntities.some(c => c.image);
                            const modelId = getSceneImageModelId(hasInputImages);
                            try {
                                const result = await generateSceneImage(sanitizePromptForConfirmedEntities(shot.startFramePrompt, shot), projectData.aspectRatio, allEntities, [], modelId);
                                startFrameImage = result.image || result;
                                if (result.creditsUsed) totalCreditsUsed += result.creditsUsed;
                                if (result.creditsRemaining !== undefined) lastCreditsRemaining = result.creditsRemaining;
                                if (result.jobId) generatedJobIds.push(result.jobId);
                                if (startFrameImage) generatedImages.push(startFrameImage);
                            } catch (err) {
                                console.warn(`⚠️ Fallback start frame failed:`, err.message);
                                failedFrames.push({ shotId: shot.id, frameType: 'start', sceneId, jobId: err.jobId || null });
                            }
                        }
                    }
                }
                
                // Always generate end frame (fresh for each shot)
                const referencesForEndFrame = startFrameImage ? [startFrameImage] : [];
                let endFrameImage = shot.endFrameImage;
                
                if (shot.endFramePrompt && !shot.endFrameImage) {
                    console.log(`🎬 Scene ${scenes.findIndex(s => s.id === sceneId) + 1}, Shot ${shotIndex + 1}: Generating end frame`);
                    try {
                        const result = await generateSceneImage(sanitizePromptForConfirmedEntities(shot.endFramePrompt, shot), projectData.aspectRatio, allEntities, referencesForEndFrame, getSceneImageModelId(true));
                        endFrameImage = result.image || result;
                        if (result.creditsUsed) totalCreditsUsed += result.creditsUsed;
                        if (result.creditsRemaining !== undefined) lastCreditsRemaining = result.creditsRemaining;
                        if (result.jobId) generatedJobIds.push(result.jobId);
                        if (endFrameImage) generatedImages.push(endFrameImage);
                    } catch (genErr) {
                        console.warn(`⚠️ End frame failed:`, genErr.message);
                        failedFrames.push({ shotId: shot.id, frameType: 'end', sceneId, jobId: genErr.jobId || null });
                    }
                } else {
                    console.log(`🎬 Scene ${scenes.findIndex(s => s.id === sceneId) + 1}, Shot ${shotIndex + 1}: Skipping end frame (prompt=${!!shot.endFramePrompt}, existing=${!!shot.endFrameImage})`);
                }

                targetSceneState = {
                    ...targetSceneState,
                    shots: targetSceneState.shots.map(os => os.id === shot.id ? { ...os, startFrameImage: startFrameImage, endFrameImage: endFrameImage } : os)
                };
                
                const updatedShot = targetSceneState.shots.find(s => s.id === shot.id);
                console.log(`💾 Saving images for Shot ${shotIndex + 1}:`, {
                    startFrameImage: startFrameImage ? startFrameImage.substring(0, 50) + '...' : 'null',
                    endFrameImage: endFrameImage ? endFrameImage.substring(0, 50) + '...' : 'null',
                    shotId: shot.id,
                    updatedInState: !!updatedShot?.startFrameImage || !!updatedShot?.endFrameImage
                });
                
                syncSceneToParent(sceneId, targetSceneState);
                
                // Store end frame for next shot's continuity
                previousEndFrame = endFrameImage;
            }

            // Smart recovery for failed frames:
            // Step 1: Re-fetch using jobId (free, image may already exist)
            // Step 2: Re-generate only if no jobId or re-fetch failed (costs credits)
            if (failedFrames.length > 0) {
                console.log(`🔄 Recovering ${failedFrames.length} failed frame(s)...`);
                
                for (const ff of failedFrames) {
                    let recovered = false;
                    
                    // Step 1: Try re-fetching if we have a jobId
                    if (ff.jobId) {
                        console.log(`🔄 Re-fetching frame (Scene ${scenes.findIndex(s => s.id === ff.sceneId) + 1}, ${ff.frameType}) via jobId ${ff.jobId}...`);
                        try {
                            const rePollResult = await rePollJob(ff.jobId, 15, 4000);
                            if (rePollResult.success && rePollResult.imageUrl) {
                                console.log(`✅ Re-fetch recovered image for ${ff.frameType} frame`);
                                const update = ff.frameType === 'start' ? { startFrameImage: rePollResult.imageUrl } : { endFrameImage: rePollResult.imageUrl };
                                targetSceneState = {
                                    ...targetSceneState,
                                    shots: targetSceneState.shots.map(sh => sh.id === ff.shotId ? { ...sh, ...update } : sh)
                                };
                                syncSceneToParent(sceneId, targetSceneState);
                                generatedImages.push(rePollResult.imageUrl);
                                recovered = true;
                            }
                        } catch { /* re-fetch failed, will try re-generate */ }
                    }
                    
                    // Step 2: Re-generate if re-fetch didn't work (only costs credits if truly needed)
                    if (!recovered) {
                        console.log(`🔄 Re-generating ${ff.frameType} frame for shot ${ff.shotId} (no jobId or re-fetch failed)...`);
                        const shot = targetSceneState.shots.find(s => s.id === ff.shotId);
                        if (!shot) continue;
                        const prompt = ff.frameType === 'start' ? shot.startFramePrompt : shot.endFramePrompt;
                        if (!prompt) continue;
                        
                        const charactersInShot = getCharactersForShot(shot);
                        const allElements = projectData.characters?.filter(c => c.category === 'element') || [];
                        const elementsInShot = allElements.filter(el => shot.elementIds?.includes(el.id));
                        const entities = [...charactersInShot, ...elementsInShot];
                        const refs = ff.frameType === 'end' && shot.startFrameImage ? [shot.startFrameImage] : [];
                        
                        try {
                            const result = await generateSceneImage(prompt, projectData.aspectRatio, entities, refs, getSceneImageModelId(entities.some(c => c.image)));
                            const imageUrl = result.image || result;
                            if (imageUrl && typeof imageUrl === 'string' && imageUrl.startsWith('http')) {
                                const update = ff.frameType === 'start' ? { startFrameImage: imageUrl } : { endFrameImage: imageUrl };
                                targetSceneState = {
                                    ...targetSceneState,
                                    shots: targetSceneState.shots.map(sh => sh.id === ff.shotId ? { ...sh, ...update } : sh)
                                };
                                syncSceneToParent(sceneId, targetSceneState);
                                generatedImages.push(imageUrl);
                                if (result.creditsUsed) totalCreditsUsed += result.creditsUsed;
                                console.log(`✅ Re-generated ${ff.frameType} frame successfully`);
                            }
                        } catch (regenErr) {
                            console.error(`❌ Re-generate also failed for ${ff.frameType} frame:`, regenErr.message);
                        }
                    }
                }
            }

            if (onSaveMedia && generatedImages.length > 0) {
                onSaveMedia(generatedImages);
            }
            
            // Track total credits used from all image generations
            if (onTrackCreditsUsed && totalCreditsUsed > 0) {
                // Pass last remaining credits and all job IDs
                onTrackCreditsUsed(totalCreditsUsed, lastCreditsRemaining, generatedJobIds.length > 0 ? generatedJobIds[generatedJobIds.length - 1] : null);
            }

        } catch (err) {
            console.error("Scene image generation failed:", err);
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred during image generation.";
            setSceneErrors(prev => ({ ...prev, [sceneId]: errorMessage }));
        } finally {
            setGeneratingScenes(prev => {
                const newSet = new Set(prev);
                newSet.delete(sceneId);
                return newSet;
            });
            // Stage 4: auto-save after scene frame generation completes.
            // 300ms delay lets React commit the scene updates + parent's
            // activeProjectRef useEffect run before autosave reads it.
            if (onAutoSave) setTimeout(() => onAutoSave({ reason: `scene-images:${sceneId}` }), 300);
        }
    };

    const handleGenerateAllImages = async () => {
        const isPanelBased = !!projectData?.usesPanelImages;

        // Count frames-to-generate, with two modes:
        //   • Panel-based projects: every panel with a prompt but no image
        //   • Legacy start/end-frame: original continuity-aware count
        let totalFrames = 0;
        if (isPanelBased) {
            for (const scene of scenes) {
                for (const shot of scene.shots) {
                    const panels = Array.isArray(shot.panels) && shot.panels.length > 0
                        ? shot.panels
                        : getPanelsForShot(shot);
                    for (const p of panels) {
                        if (p?.prompt && !p?.image) totalFrames++;
                    }
                }
            }
        } else {
            for (const scene of scenes) {
                for (let i = 0; i < scene.shots.length; i++) {
                    const shot = scene.shots[i];
                    if (i === 0) {
                        if (!shot.startFrameImage) totalFrames++;
                    }
                    if (!shot.endFrameImage) totalFrames++;
                }
            }
        }

        if (totalFrames === 0) {
            if (!silent) alert(isPanelBased ? 'All panels already have images.' : 'All frames already have images.');
            return;
        }

        const totalCost = totalFrames * CREDIT_COSTS.IMAGE_GENERATION;
        if (!silent) {
            const msg = isPanelBased
                ? `Generate ${totalFrames} panel image${totalFrames > 1 ? 's' : ''} across all scenes (Est. ${totalCost} credits)?\n\nAll panels in a scene generate in parallel.`
                : `Generate ${totalFrames} frame${totalFrames > 1 ? 's' : ''} across all scenes (Est. ${totalCost} credits)?\n\nNote: Shot continuity is enabled - end frames will be reused as start frames for subsequent shots within each scene.`;
            if (!confirm(msg)) return;
        }
        // Credits are deducted by backend - just proceed with generation

        setIsGeneratingAll(true);
        setGenerationProgress({ current: 0, total: scenes.length, message: 'Starting batch generation...' });

        try {
            await preloadProjectAssets(projectData, (msg) => setGenerationProgress(prev => ({ ...prev, message: msg })));

            // Generate ALL scenes in parallel — shots within each scene are sequential
            setGenerationProgress({ current: 0, total: scenes.length, message: `Generating ${scenes.length} scenes in parallel...` });
            
            let completedCount = 0;
            await Promise.all(scenes.map(async (scene, i) => {
                try {
                    await handleGenerateSceneImages(scene.id);
                } catch (err) {
                    console.error(`Scene ${i + 1} generation failed:`, err.message);
                } finally {
                    completedCount++;
                    setGenerationProgress({ current: completedCount, total: scenes.length, message: `Completed ${completedCount}/${scenes.length} scenes...` });
                }
            }));
            
            setGenerationProgress({ current: scenes.length, total: scenes.length, message: 'All images generated!' });
        } catch (err) {
            console.error('Batch generation failed:', err);
        } finally {
            // Reconcile live credit balance from backend after parallel batch
            // — ensures UI matches server regardless of worker completion order.
            if (onRefreshCredits) {
                try { await onRefreshCredits(); } catch { /* non-fatal */ }
            }
            setTimeout(() => setIsGeneratingAll(false), 2000);
        }
    };

    const handleGenerateAllVideos = async () => {
        const isPanelBased = !!projectData?.usesPanelImages;
        // A shot is video-ready when it has the required image input:
        //   • Panel-based projects: at least one panel image
        //   • Legacy: a startFrameImage
        const isShotVideoReady = (s) => isPanelBased
            ? Array.isArray(s.panels) && s.panels.some(p => !!p?.image)
            : !!s.startFrameImage;
        const allShots = scenes.flatMap(s => s.shots).filter(s => isShotVideoReady(s) && !s.video);
        const totalCost = allShots.length * CREDIT_COSTS.VIDEO_GENERATION;

        if (allShots.length === 0) {
            if (!silent) alert(isPanelBased
                ? 'All shots already have videos or are missing panel images.'
                : 'All shots already have videos or are missing required frame images.');
            return;
        }

        if (!silent && !confirm(`Generate videos for ${allShots.length} shots across all scenes (Est. ${totalCost} credits)?`)) return;

        setIsGeneratingAllVideos(true);
        setGenerationProgress({ current: 0, total: scenes.length, message: `Generating videos for ${scenes.length} scenes in parallel...` });

        try {
            let completedCount = 0;
            const updateProgress = () => {
                setGenerationProgress({
                    current: completedCount,
                    total: scenes.length,
                    message: `Completed ${completedCount}/${scenes.length} scenes...`
                });
            };

            const isPanelBased = !!projectData?.usesPanelImages;
            const shotVideoReady = (sh) => !sh.video && (isPanelBased
                ? Array.isArray(sh.panels) && sh.panels.some(p => !!p?.image)
                : !!sh.startFrameImage);
            const queue = scenes.filter(s => s.shots.some(shotVideoReady));
            scenes.forEach(s => {
                if (!queue.includes(s)) {
                    completedCount++;
                }
            });
            updateProgress();

            // Scene-level concurrency for the bulk-video flow.
            // Tunable: keep this as the single source of truth.
            //   • Infinity → all scenes generate videos in parallel (max throughput)
            //   • 4        → safer middle-ground if fal.ai Veo3 throws 403s under
            //                heavy fan-out (per-account concurrency wall returns)
            //   • 1        → fully sequential (legacy behaviour)
            // Shots within each scene remain sequential because of shot
            // continuity (shot N+1 may depend on shot N's end frame).
            const VIDEO_SCENE_CONCURRENCY = Infinity;

            const worker = async () => {
                while (queue.length > 0) {
                    const scene = queue.shift();
                    if (!scene) break;
                    const i = scenes.indexOf(scene);
                    try {
                        await handleGenerateSceneVideos(scene.id);
                    } catch (err) {
                        console.error(`Scene ${i + 1} video generation failed:`, err.message);
                    } finally {
                        completedCount++;
                        updateProgress();
                    }
                }
            };
            const effectiveConcurrency = Math.min(
                VIDEO_SCENE_CONCURRENCY === Infinity ? queue.length : VIDEO_SCENE_CONCURRENCY,
                queue.length
            );
            await Promise.all(
                Array.from({ length: effectiveConcurrency }, () => worker())
            );

            setGenerationProgress({ current: scenes.length, total: scenes.length, message: 'All videos generated!' });
        } catch (err) {
            console.error('Batch video generation failed:', err);
        } finally {
            // Reconcile live credit balance from backend after batch.
            if (onRefreshCredits) {
                try { await onRefreshCredits(); } catch { /* non-fatal */ }
            }
            setTimeout(() => setIsGeneratingAllVideos(false), 2000);
        }
    };

    // ----- Auto-Pilot bridge -----
    // Lets a parent (AutoPilot) drive batch generation programmatically, by
    // exposing the existing batch handlers via ref. Storyboard still works
    // standalone for manual edits.
    useImperativeHandle(ref, () => ({
        runAllImages: () => handleGenerateAllImages(),
        runAllVideos: () => handleGenerateAllVideos(),
        getProgress: () => generationProgress,
        getScenes: () => scenesRef.current,
    }), [scenes, generationProgress]);

    return (
        <div className="w-full h-full flex flex-col" style={{ background: '#050505' }}>
            {/* Global Progress Overlay */}
            {(isGeneratingAll || isGeneratingAllVideos) && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-[#0F1115] p-8 rounded-2xl border border-white/10 max-w-md w-full text-center shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
                        <LoadingSpinner className="w-12 h-12 mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2">{isGeneratingAll ? 'Generating Images' : 'Generating Videos'}</h3>
                        <p className="text-zinc-400 mb-4">{generationProgress.message}</p>
                        <div className="w-full bg-zinc-800 rounded-full h-2.5">
                            <div 
                                className="bg-violet-500 h-2.5 rounded-full transition-all duration-500" 
                                style={{ width: generationProgress.total > 0 ? `${(generationProgress.current / generationProgress.total) * 100}%` : '0%' }}
                            ></div>
                        </div>
                        <p className="mt-2 text-sm text-zinc-500">
                            {generationProgress.current} / {generationProgress.total} completed
                        </p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center shrink-0" style={{ background: 'rgba(15, 17, 21, 0.8)' }}>
                <div>
                    <h2 className="text-xl font-bold text-white tracking-tight">Storyboard Editor</h2>
                    <p className="text-xs text-zinc-500">Refine the automatically generated shots or add your own.</p>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => setShowAIToolkitModal(true)}
                        className="bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white font-semibold py-2 px-4 rounded-xl flex items-center justify-center transition-all text-xs"
                        title="Change AI model preferences"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        AI Settings
                    </button>
                    <button
                        onClick={handleGenerateAllImages}
                        disabled={isGeneratingAll || isGeneratingAllVideos}
                        className="bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/30 disabled:bg-zinc-800 disabled:border-zinc-700 disabled:text-zinc-600 disabled:cursor-not-allowed text-violet-400 font-semibold py-2 px-4 rounded-xl flex items-center justify-center transition-all text-xs"
                    >
                        {isGeneratingAll ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <MagicWandIcon className="w-4 h-4 mr-2" />}
                        {isGeneratingAll ? 'Generating...' : 'Generate All Images'}
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleGenerateAllVideos}
                            disabled={isGeneratingAll || isGeneratingAllVideos}
                            className="bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/30 disabled:bg-zinc-800 disabled:border-zinc-700 disabled:text-zinc-600 disabled:cursor-not-allowed text-violet-400 font-semibold py-2 px-4 rounded-xl flex items-center justify-center transition-all text-xs"
                        >
                            {isGeneratingAllVideos ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <FilmIcon className="w-4 h-4 mr-2" />}
                            {isGeneratingAllVideos ? 'Generating...' : 'Generate All Videos'}
                        </button>
                        {scenes.length > 0 && (() => {
                            const total = scenes.length;
                            const ready = scenes.filter(s => preProductionStatus[s.id] === 'ready').length;
                            const failed = scenes.filter(s => preProductionStatus[s.id] === 'failed').length;
                            const inFlight = total - ready - failed;
                            const allReady = ready === total;
                            return (
                                <span
                                    data-testid="pre-production-completion-counter"
                                    title={`${ready} ready · ${inFlight} preparing · ${failed} failed (out of ${total})`}
                                    className={`flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-1 border ${
                                        allReady
                                            ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                                            : failed > 0 && inFlight === 0
                                                ? 'text-amber-300 bg-amber-500/10 border-amber-500/30'
                                                : 'text-violet-300 bg-violet-500/10 border-violet-500/30'
                                    }`}
                                >
                                    {!allReady && inFlight > 0 && <LoadingSpinner className="w-3 h-3" />}
                                    <span>Pre-prod {ready}/{total}</span>
                                    {failed > 0 && <span className="text-amber-400">· {failed} failed</span>}
                                </span>
                            );
                        })()}
                    </div>
                    <button
                        onClick={onBack}
                        className="bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white font-semibold py-2 px-4 rounded-xl flex items-center justify-center transition-all text-xs"
                    >
                        <ArrowLeftIcon className="w-4 h-4 mr-2" />
                        Back
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="bg-violet-600 hover:bg-violet-700 text-white font-bold py-2 px-5 rounded-xl flex items-center justify-center transition-all hover:-translate-y-0.5 text-xs"
                    >
                        Continue to Studio
                        <ArrowRightIcon className="w-4 h-4 ml-2" />
                    </button>
                </div>
            </div>

            {/* Main Content - Horizontal Scenes with Vertical Shots */}
            <div className="flex-grow flex px-6 py-6 space-x-6 overflow-x-auto">
                {scenes.map((scene, sceneIndex) => (
                    <div key={scene.id} className="w-[450px] flex-shrink-0 rounded-2xl p-4 flex flex-col space-y-4 border border-white/10" style={{ background: 'rgba(15, 17, 21, 0.7)' }}>
                        {/* Scene Header */}
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-violet-400">Scene {sceneIndex + 1}</h3>
                                {(() => {
                                    const st = preProductionStatus[scene.id];
                                    const isRetrying = typeof st === 'object' && st?.state === 'retrying';
                                    return isRetrying ? (
                                        <span
                                            data-testid={`pre-production-status-retrying-${scene.id}`}
                                            className="flex items-center gap-1 text-[10px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5"
                                            title={`Retrying pre-production (attempt ${st.attempt}/${st.maxAttempts})`}
                                        >
                                            <LoadingSpinner className="w-3 h-3" />
                                            Retry {st.attempt}/{st.maxAttempts}
                                        </span>
                                    ) : null;
                                })()}
                                {preProductionStatus[scene.id] === 'loading' && (
                                    <span
                                        data-testid={`pre-production-status-loading-${scene.id}`}
                                        className="flex items-center gap-1 text-[10px] font-medium text-violet-300 bg-violet-500/10 border border-violet-500/30 rounded-full px-2 py-0.5"
                                        title="Preparing cinematic prompts, dialogues and audio cues"
                                    >
                                        <LoadingSpinner className="w-3 h-3" />
                                        Prepping
                                    </span>
                                )}
                                {preProductionStatus[scene.id] === 'ready' && (
                                    <button
                                        type="button"
                                        data-testid={`pre-production-status-ready-${scene.id}`}
                                        onClick={() => handleRetryPreProduction(scene.id)}
                                        className="group flex items-center gap-1 text-[10px] font-medium text-emerald-300 hover:text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 hover:border-emerald-400/50 rounded-full px-2 py-0.5 transition-colors"
                                        title="Pre-production data ready — click to regenerate"
                                    >
                                        <span>✓ Pre-prod ready</span>
                                        <span className="opacity-0 group-hover:opacity-100 transition-opacity">↻</span>
                                    </button>
                                )}
                                {preProductionStatus[scene.id] === 'failed' && (
                                    <button
                                        type="button"
                                        data-testid={`pre-production-status-failed-${scene.id}`}
                                        onClick={() => handleRetryPreProduction(scene.id)}
                                        className="flex items-center gap-1 text-[10px] font-medium text-amber-300 hover:text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 hover:border-amber-400/50 rounded-full px-2 py-0.5 transition-colors"
                                        title="Pre-production failed — click to retry"
                                    >
                                        <span>⚠ Pre-prod retry</span>
                                        <span>↻</span>
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleGenerateSceneImages(scene.id)}
                                    disabled={generatingScenes.has(scene.id) || isGeneratingAll}
                                    title="Generate all images for this scene"
                                    className="bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 disabled:bg-zinc-800 disabled:border-zinc-700 disabled:text-zinc-600 disabled:cursor-not-allowed text-violet-400 font-semibold py-1 px-3 rounded-lg transition-all text-xs flex items-center"
                                >
                                    {generatingScenes.has(scene.id) ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <MagicWandIcon className="w-4 h-4 mr-1" />}
                                    {generatingScenes.has(scene.id) ? 'Generating...' : 'Gen Images'}
                                </button>
                                <button
                                    onClick={() => handleGenerateSceneVideos(scene.id)}
                                    disabled={generatingVideosForScenes.has(scene.id) || isGeneratingAllVideos}
                                    title="Generate all videos for this scene"
                                    className="bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 disabled:bg-zinc-800 disabled:border-zinc-700 disabled:text-zinc-600 disabled:cursor-not-allowed text-violet-400 font-semibold py-1 px-3 rounded-lg transition-all text-xs flex items-center"
                                >
                                    {generatingVideosForScenes.has(scene.id) ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <FilmIcon className="w-4 h-4 mr-1" />}
                                    {generatingVideosForScenes.has(scene.id) ? 'Generating...' : 'Gen Videos'}
                                </button>
                                <button
                                    onClick={() => handleAddShot(scene.id)}
                                    className="bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 font-semibold py-1 px-3 rounded-lg transition-all text-xs flex items-center"
                                    title="Add shot to this scene"
                                >
                                    <PlusIcon className="w-4 h-4 mr-1" />
                                    Shot
                                </button>
                                <button
                                    onClick={() => handleRemoveScene(scene.id)}
                                    className="text-red-500/70 hover:text-red-400 text-xs font-bold p-1"
                                    title="Remove this scene"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {sceneErrors[scene.id] && (
                            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 p-2 rounded-xl">
                                {sceneErrors[scene.id]}
                            </div>
                        )}

                        {/* Shots - Vertical Layout */}
                        <div className="relative space-y-3 overflow-y-auto overflow-x-hidden pr-2 max-h-[calc(100vh-250px)] min-w-0">
                            {(generatingScenes.has(scene.id) || generatingVideosForScenes.has(scene.id)) && (
                                <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm z-20 flex items-center justify-center rounded-lg">
                                    <div className="text-center">
                                        <LoadingSpinner className="w-8 h-8 mx-auto mb-2" />
                                        <p className="text-gray-300 font-semibold">
                                            {generatingVideosForScenes.has(scene.id) ? 'Generating scene videos...' : 'Generating scene images...'}
                                        </p>
                                    </div>
                                </div>
                            )}
                            {scene.shots.map((shot, shotIndex) => {
                                if (!videoInputRefs.current[shot.id]) {
                                    videoInputRefs.current[shot.id] = null;
                                }
                                const isUpdatingPrompts = updatingPromptsForShotId === shot.id;
                                const isGeneratingStart = generatingFrames[`${shot.id}-start`];
                                const isGeneratingEnd = generatingFrames[`${shot.id}-end`];

                                return (
                                    <div key={shot.id} className="bg-white/5 border border-white/10 p-3 rounded-xl space-y-3 min-w-0 overflow-hidden">
                                        {/* Hidden file inputs */}
                                        <input
                                            type="file"
                                            accept="video/*"
                                            className="hidden"
                                            ref={el => { videoInputRefs.current[shot.id] = el; }}
                                            onChange={(e) => handleVideoFileChange(e, shot.id)}
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={el => { frameInputRefs.current[`${shot.id}-start`] = el; }}
                                            onChange={(e) => handleFrameImageUpload(e, scene.id, shot.id, 'start')}
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={el => { frameInputRefs.current[`${shot.id}-end`] = el; }}
                                            onChange={(e) => handleFrameImageUpload(e, scene.id, shot.id, 'end')}
                                        />

                                        {/* Shot Header */}
                                        <div className="flex justify-between items-center">
                                            <h4 className="font-semibold text-gray-300">Shot {shotIndex + 1}</h4>
                                            <button 
                                                onClick={() => handleRemoveShot(scene.id, shot.id)} 
                                                className="text-red-500 hover:text-red-400 text-xs font-bold"
                                            >
                                                REMOVE
                                            </button>
                                        </div>

                                        {/* Metadata Display */}
                                        {(shot.cinematography || shot.sound || shot.vfx) && (
                                            <div className="text-xs bg-black/20 border border-white/5 p-2 rounded-lg space-y-1">
                                                {shot.cinematography && (
                                                    <div className="flex gap-2 text-zinc-400">
                                                        <span className="text-zinc-400 font-semibold">Cam:</span> 
                                                        {shot.cinematography.shotSize}, {shot.cinematography.cameraAngle}, {shot.cinematography.movement}
                                                    </div>
                                                )}
                                                {shot.sound && (
                                                    <div className="flex gap-2 text-zinc-400">
                                                        <span className="text-zinc-400 font-semibold">Sound:</span> 
                                                        <span className="truncate">{shot.sound.music} | {shot.sound.sfx}</span>
                                                    </div>
                                                )}
                                                {shot.vfx && shot.vfx !== "None" && (
                                                    <div className="flex gap-2 text-zinc-400">
                                                        <span className="text-zinc-400 font-semibold">VFX:</span> 
                                                        {shot.vfx}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Shot Description */}
                                        <div>
                                            {editingDescriptionShotId === shot.id ? (
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-semibold text-gray-400 uppercase">Description</label>
                                                    <textarea
                                                        value={shot.description || ''}
                                                        onChange={(e) => handleDescriptionChange(scene.id, shot.id, e.target.value)}
                                                        rows={3}
                                                        autoFocus
                                                        className="w-full p-2 bg-black/20 border border-violet-500/30 rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-violet-500/30"
                                                        placeholder="Describe what happens in this shot..."
                                                        data-testid={`desc-textarea-${shot.id}`}
                                                    />
                                                    <div className="flex gap-1.5 items-center flex-wrap">
                                                        {/* Feb 2026 — Manual-shot panel-count picker.
                                                            Auto-shows for shots whose description
                                                            has no "Panel N (00:00)" structure yet
                                                            (i.e. user-typed / narrated draft).
                                                            Picking a count and clicking Generate
                                                            expands the plain description into a
                                                            fully-formatted cinematic shot with
                                                            timecoded panels. */}
                                                        {isManualShot(shot) && shot.description && shot.description.trim() && (
                                                            <>
                                                                <label className="text-[11px] text-zinc-400 pl-1">Panels:</label>
                                                                <select
                                                                    value={manualPanelCounts[shot.id] || 4}
                                                                    onChange={(e) => setManualPanelCounts(prev => ({ ...prev, [shot.id]: Number(e.target.value) }))}
                                                                    className="px-2 py-1 bg-black/30 border border-white/10 text-zinc-200 rounded-lg text-xs cursor-pointer"
                                                                    data-testid={`manual-panel-count-${shot.id}`}
                                                                    title="How many panels should this shot break into?"
                                                                >
                                                                    {[1,2,3,4,5,6,7,8].map(n => (
                                                                        <option key={n} value={n}>{n}</option>
                                                                    ))}
                                                                </select>
                                                                <button
                                                                    onClick={() => handleExpandManualShot(scene.id, shot.id)}
                                                                    disabled={expandingManualShotId === shot.id}
                                                                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-xs rounded-lg font-semibold flex items-center gap-1"
                                                                    data-testid={`expand-manual-shot-${shot.id}`}
                                                                    title="Expand into a cinematic shot description with the selected number of panels"
                                                                >
                                                                    {expandingManualShotId === shot.id ? (
                                                                        <><LoadingSpinner className="w-3 h-3" /> Expanding…</>
                                                                    ) : (
                                                                        <><MagicWandIcon className="w-3 h-3" /> Generate {manualPanelCounts[shot.id] || 4} Panel{(manualPanelCounts[shot.id] || 4) === 1 ? '' : 's'}</>
                                                                    )}
                                                                </button>
                                                            </>
                                                        )}
                                                        {!isManualShot(shot) && (
                                                        <button
                                                            onClick={() => handleDescriptionSubmit(scene.id, shot.id, shot.description)}
                                                            disabled={isUpdatingPrompts}
                                                            className="px-3 py-1 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-xs rounded-lg font-semibold flex items-center gap-1"
                                                            data-testid={`submit-desc-${shot.id}`}
                                                        >
                                                            {isUpdatingPrompts ? (
                                                                <><LoadingSpinner className="w-3 h-3" /> Generating...</>
                                                            ) : (
                                                                <><MagicWandIcon className="w-3 h-3" /> Generate Prompts</>
                                                            )}
                                                        </button>
                                                        )}
                                                        <button
                                                            onClick={() => setEditingDescriptionShotId(null)}
                                                            className="px-3 py-1 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 text-xs rounded-lg"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setEditingDescriptionShotId(shot.id)}
                                                    className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-violet-400 transition-colors"
                                                    data-testid={`edit-desc-${shot.id}`}
                                                >
                                                    <PencilIcon className="w-3 h-3" />
                                                    <span className="font-semibold uppercase">
                                                        {shot.description ? 'Edit Description' : 'Add Description'}
                                                    </span>
                                                </button>
                                            )}
                                        </div>

                                        {/* Frame Images — NEW projects use panel strip, OLD projects keep dual-card */}
                                        {projectData?.usesPanelImages ? (
                                            <PanelImageStrip
                                                shot={shot}
                                                sceneId={scene.id}
                                                panels={getPanelsForShot(shot)}
                                                projectData={projectData}
                                                resolveCharacters={(panelIndex) => getCharactersForPanel(shot, panelIndex)}
                                                resolveElements={(panelIndex) => getElementsForPanel(shot, panelIndex)}
                                                generatingFrames={generatingFrames}
                                                onGenerate={handleGeneratePanelImage}
                                                onUploadClick={(panelIndex) => frameInputRefs.current[`${shot.id}-panel-${panelIndex}`]?.click()}
                                                onUploadFile={handleUploadPanelImage}
                                                onDelete={handleDeletePanelImage}
                                                onEditPrompt={handleEditPanelPrompt}
                                                onEditImage={(panelIndex) => setEditingInfo({ shot, panelIndex })}
                                                onViewImage={(panelIndex, url) => setViewingFrameImage({ url, title: `Panel ${panelIndex + 1} — Shot ${shot.id}` })}
                                                onAddCharacter={handleAddCharacterToPanel}
                                                onRemoveCharacter={handleRemoveCharacterFromPanel}
                                                onAddElement={handleAddElementToPanel}
                                                onRemoveElement={handleRemoveElementFromPanel}
                                                onEditCharacter={(char) => setEditingCharacter(char)}
                                                onCreateFromPanel={(sceneId, shotId, panelIndex, category) => {
                                                    // Feb 2026 — Open the character/element modal with
                                                    // a stub; handleSaveCharacter reads
                                                    // pendingCreateForPanel and attaches it to the
                                                    // panel in one atomic update.
                                                    setPendingCreateForPanel({ sceneId, shotId, panelIndex, category });
                                                    setEditingCharacter({
                                                        id: uuidv4(),
                                                        name: category === 'element' ? 'New Element' : 'New Character',
                                                        description: '',
                                                        category,
                                                        referenceImages: [],
                                                        costumeAndAccessoryImages: [],
                                                    });
                                                }}
                                                frameInputRefs={frameInputRefs}
                                            />
                                        ) : (
                                        /* Frame Images - Side by Side (legacy layout for old projects) */
                                        <div className="grid grid-cols-2 gap-3">
                                            {/* Start Frame */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-400 uppercase flex items-center gap-2">
                                                    Start Frame
                                                    {shotIndex > 0 && (
                                                        <span 
                                                            className={`text-[10px] font-normal px-1.5 py-0.5 rounded ${
                                                                scene.shots[shotIndex - 1]?.endFrameImage 
                                                                    ? 'text-green-400 bg-green-500/20' 
                                                                    : 'text-cyan-400 bg-cyan-500/20'
                                                            }`} 
                                                            title={scene.shots[shotIndex - 1]?.endFrameImage 
                                                                ? "Linked from previous shot's end frame" 
                                                                : "Will be linked from previous shot's end frame when generated"
                                                            }
                                                        >
                                                            🔗 {scene.shots[shotIndex - 1]?.endFrameImage ? 'Linked' : 'Continuity'}
                                                        </span>
                                                    )}
                                                    {shot.video && (
                                                        <span 
                                                            className="text-[10px] font-normal px-1.5 py-0.5 rounded text-violet-400 bg-violet-500/20"
                                                            title={`Video saved (${shot.videoDuration ? shot.videoDuration.toFixed(1) + 's' : 'duration unknown'})`}
                                                        >
                                                            🎬 Video Ready
                                                        </span>
                                                    )}
                                                    {shot.video && typeof shot.trimStart === 'number' && typeof shot.trimEnd === 'number' && (
                                                        <span
                                                            className="text-[10px] font-normal px-1.5 py-0.5 rounded text-purple-300 bg-purple-500/20"
                                                            title={`Trim saved: ${shot.trimStart}s → ${shot.trimEnd}s (${(shot.trimEnd - shot.trimStart).toFixed(1)}s clip)`}
                                                            data-testid={`shot-trim-badge-${shot.id}`}
                                                        >
                                                            ✂ {(shot.trimEnd - shot.trimStart).toFixed(1)}s
                                                        </span>
                                                    )}
                                                </label>
                                                <div className="relative group aspect-video bg-gray-900 rounded-md overflow-hidden">
                                                    {shot.startFrameImage ? (
                                                        <>
                                                            <img 
                                                                src={shot.startFrameImage} 
                                                                alt="Start" 
                                                                className="w-full h-full object-cover"
                                                                crossOrigin="anonymous"
                                                                referrerPolicy="no-referrer"
                                                                onError={(e) => {
                                                                    console.warn('Start frame failed to load:', shot.startFrameImage?.substring(0, 60));
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                            {/* Video indicator overlay */}
                                                            {shot.video && (
                                                                <div className="absolute top-1 right-1 bg-violet-600/80 px-1.5 py-0.5 rounded text-[10px] text-white font-medium flex items-center gap-1">
                                                                    🎬 {shot.videoDuration ? `${shot.videoDuration.toFixed(1)}s` : 'Video'}
                                                                </div>
                                                            )}
                                                            {/* View button overlay on hover */}
                                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                <button
                                                                    onClick={() => setViewingFrameImage({ url: shot.startFrameImage, title: `Start Frame - Shot ${shot.id}` })}
                                                                    className="p-2 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300"
                                                                    title="View full image"
                                                                >
                                                                    <EyeIcon className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <PhotoIcon className="w-8 h-8 text-gray-600" />
                                                        </div>
                                                    )}
                                                    {isGeneratingStart && (
                                                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                                                            <LoadingSpinner className="w-6 h-6" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="min-h-[2rem]">
                                                    {editingPromptKey === `${shot.id}-start` ? (
                                                        <div className="space-y-1">
                                                            <textarea
                                                                defaultValue={shot.startFramePrompt || ''}
                                                                rows={3}
                                                                autoFocus
                                                                className="w-full p-1.5 bg-black/20 border border-violet-500/30 rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-violet-500/30"
                                                                data-testid={`start-prompt-textarea-${shot.id}`}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Escape') setEditingPromptKey(null);
                                                                }}
                                                                ref={(el) => { if (el) el._shotId = shot.id; el && (el._sceneId = scene.id); }}
                                                                id={`start-prompt-input-${shot.id}`}
                                                            />
                                                            <div className="flex gap-1">
                                                                <button
                                                                    onClick={() => {
                                                                        const val = document.getElementById(`start-prompt-input-${shot.id}`)?.value;
                                                                        if (val !== undefined) handlePromptEdit(scene.id, shot.id, 'start', val);
                                                                    }}
                                                                    className="px-2 py-0.5 bg-violet-600 hover:bg-violet-700 text-white text-[10px] rounded font-semibold"
                                                                >Save</button>
                                                                <button
                                                                    onClick={() => setEditingPromptKey(null)}
                                                                    className="px-2 py-0.5 bg-gray-700 hover:bg-gray-600 text-white text-[10px] rounded"
                                                                >Cancel</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p
                                                            className="text-xs text-gray-500 line-clamp-2 cursor-pointer hover:text-gray-300 transition-colors group/prompt"
                                                            onClick={() => setEditingPromptKey(`${shot.id}-start`)}
                                                            title="Click to edit prompt"
                                                            data-testid={`start-prompt-display-${shot.id}`}
                                                        >
                                                            {shot.startFramePrompt || <span className="italic">No prompt yet</span>}
                                                            <PencilIcon className="w-2.5 h-2.5 inline ml-1 opacity-0 group-hover/prompt:opacity-100 transition-opacity" />
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleGenerateFrame(scene.id, shot.id, 'start')}
                                                        disabled={isGeneratingStart || !shot.startFramePrompt}
                                                        className="flex-1 bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/20 disabled:bg-zinc-800 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Generate start frame"
                                                    >
                                                        <MagicWandIcon className="w-3 h-3 mr-1" />
                                                        Gen
                                                    </button>
                                                    <button
                                                        onClick={() => frameInputRefs.current[`${shot.id}-start`]?.click()}
                                                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Upload start frame"
                                                    >
                                                        <UploadIcon className="w-3 h-3 mr-1" />
                                                        Up
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingInfo({ shot, frame: 'start' })}
                                                        className="flex-1 bg-violet-500/10 hover:bg-violet-500/20 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Edit start frame"
                                                    >
                                                        <PencilIcon className="w-3 h-3 mr-1" />
                                                        Ed
                                                    </button>
                                                    {shot.startFrameImage && (
                                                        <button
                                                            onClick={() => handleDeleteFrame(scene.id, shot.id, 'start')}
                                                            className="bg-red-600/50 hover:bg-red-600 text-white text-xs py-1 px-2 rounded"
                                                            title="Delete start frame"
                                                        >
                                                            <TrashIcon className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* End Frame */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-400 uppercase">End Frame</label>
                                                <div className="relative group aspect-video bg-gray-900 rounded-md overflow-hidden">
                                                    {shot.endFrameImage ? (
                                                        <>
                                                            <img 
                                                                src={shot.endFrameImage} 
                                                                alt="End" 
                                                                className="w-full h-full object-cover"
                                                                crossOrigin="anonymous"
                                                                referrerPolicy="no-referrer"
                                                                onError={(e) => {
                                                                    console.warn('End frame failed to load:', shot.endFrameImage?.substring(0, 60));
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                            {/* View button overlay on hover */}
                                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                <button
                                                                    onClick={() => setViewingFrameImage({ url: shot.endFrameImage, title: `End Frame - Shot ${shot.id}` })}
                                                                    className="p-2 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300"
                                                                    title="View full image"
                                                                >
                                                                    <EyeIcon className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <PhotoIcon className="w-8 h-8 text-gray-600" />
                                                        </div>
                                                    )}
                                                    {isGeneratingEnd && (
                                                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                                                            <LoadingSpinner className="w-6 h-6" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="min-h-[2rem]">
                                                    {editingPromptKey === `${shot.id}-end` ? (
                                                        <div className="space-y-1">
                                                            <textarea
                                                                defaultValue={shot.endFramePrompt || ''}
                                                                rows={3}
                                                                autoFocus
                                                                className="w-full p-1.5 bg-black/20 border border-violet-500/30 rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-violet-500/30"
                                                                data-testid={`end-prompt-textarea-${shot.id}`}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Escape') setEditingPromptKey(null);
                                                                }}
                                                                id={`end-prompt-input-${shot.id}`}
                                                            />
                                                            <div className="flex gap-1">
                                                                <button
                                                                    onClick={() => {
                                                                        const val = document.getElementById(`end-prompt-input-${shot.id}`)?.value;
                                                                        if (val !== undefined) handlePromptEdit(scene.id, shot.id, 'end', val);
                                                                    }}
                                                                    className="px-2 py-0.5 bg-violet-600 hover:bg-violet-700 text-white text-[10px] rounded font-semibold"
                                                                >Save</button>
                                                                <button
                                                                    onClick={() => setEditingPromptKey(null)}
                                                                    className="px-2 py-0.5 bg-gray-700 hover:bg-gray-600 text-white text-[10px] rounded"
                                                                >Cancel</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p
                                                            className="text-xs text-gray-500 line-clamp-2 cursor-pointer hover:text-gray-300 transition-colors group/prompt"
                                                            onClick={() => setEditingPromptKey(`${shot.id}-end`)}
                                                            title="Click to edit prompt"
                                                            data-testid={`end-prompt-display-${shot.id}`}
                                                        >
                                                            {shot.endFramePrompt || <span className="italic">No prompt yet</span>}
                                                            <PencilIcon className="w-2.5 h-2.5 inline ml-1 opacity-0 group-hover/prompt:opacity-100 transition-opacity" />
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleGenerateFrame(scene.id, shot.id, 'end')}
                                                        disabled={isGeneratingEnd || !shot.endFramePrompt}
                                                        className="flex-1 bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/20 disabled:bg-zinc-800 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Generate end frame"
                                                    >
                                                        <MagicWandIcon className="w-3 h-3 mr-1" />
                                                        Gen
                                                    </button>
                                                    <button
                                                        onClick={() => frameInputRefs.current[`${shot.id}-end`]?.click()}
                                                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Upload end frame"
                                                    >
                                                        <UploadIcon className="w-3 h-3 mr-1" />
                                                        Up
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingInfo({ shot, frame: 'end' })}
                                                        className="flex-1 bg-violet-500/10 hover:bg-violet-500/20 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Edit end frame"
                                                    >
                                                        <PencilIcon className="w-3 h-3 mr-1" />
                                                        Ed
                                                    </button>
                                                    {shot.endFrameImage && (
                                                        <button
                                                            onClick={() => handleDeleteFrame(scene.id, shot.id, 'end')}
                                                            className="bg-red-600/50 hover:bg-red-600 text-white text-xs py-1 px-2 rounded"
                                                            title="Delete end frame"
                                                        >
                                                            <TrashIcon className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        )}

                                        {/* Characters / Other Elements — hidden when project uses per-panel selection */}
                                        {!projectData?.usesPanelImages && (
                                        <>
                                        <div>
                                            <label className="text-xs font-semibold text-gray-400 mb-1 block uppercase">Characters</label>
                                            <div className="flex flex-wrap gap-1">
                                                {shot.characterIds?.map(charId => {
                                                    const char = projectData.characters.find(c => c.id === charId);
                                                    return char ? (
                                                        <span 
                                                            key={charId} 
                                                            className="group px-2 py-0.5 bg-violet-500/20 text-violet-300 rounded text-xs border border-violet-500/30 flex items-center gap-1 cursor-pointer hover:bg-violet-500/30"
                                                            onClick={() => setEditingCharacter(char)}
                                                            title="Click to edit character"
                                                        >
                                                            {char.name}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveCharacterFromShot(scene.id, shot.id, charId);
                                                                }}
                                                                className="opacity-0 group-hover:opacity-100 hover:text-red-400"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ) : null;
                                                })}
                                                <select
                                                    onChange={(e) => {
                                                        if (e.target.value === '__new__') {
                                                            setEditingCharacterForShot({ sceneId: scene.id, shotId: shot.id, category: 'character' });
                                                            handleCreateNewCharacter('character');
                                                        } else if (e.target.value) {
                                                            handleAddCharacterToShot(scene.id, shot.id, e.target.value);
                                                        }
                                                        e.target.value = '';
                                                    }}
                                                    className="px-2 py-0.5 bg-gray-700/50 hover:bg-gray-700 text-gray-400 rounded text-xs border-0 cursor-pointer"
                                                    value=""
                                                >
                                                    <option value="">+ Add</option>
                                                    <option value="__new__" className="font-bold text-green-400">✨ Create New Character</option>
                                                    <option disabled>──────────</option>
                                                    {projectData.characters
                                                        .filter(c => (c.category ?? 'character') === 'character' && !shot.characterIds?.includes(c.id))
                                                        .map(char => (
                                                            <option key={char.id} value={char.id}>{char.name}</option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        </div>

                                        {/* Other Elements */}
                                        <div>
                                            <label className="text-xs font-semibold text-gray-400 mb-1 block uppercase">Other Elements</label>
                                            <div className="flex flex-wrap gap-1">
                                                {/* Explicitly selected elements */}
                                                {shot.elementIds?.map(elemId => {
                                                    const elem = projectData.characters.find(c => c.id === elemId);
                                                    return elem ? (
                                                        <span 
                                                            key={elemId} 
                                                            className="group px-2 py-0.5 bg-cyan-600/30 text-cyan-300 rounded text-xs border border-cyan-600/50 flex items-center gap-1 cursor-pointer hover:bg-cyan-600/40"
                                                            onClick={() => {
                                                                setEditingCharacterForShot({ sceneId: scene.id, shotId: shot.id, category: 'element' });
                                                                setEditingCharacter(elem);
                                                            }}
                                                            title="Click to edit element"
                                                        >
                                                            {elem.name}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveElementFromShot(scene.id, shot.id, elemId);
                                                                }}
                                                                className="opacity-0 group-hover:opacity-100 hover:text-red-400"
                                                                title="Remove from shot"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ) : null;
                                                })}
                                                {/* Smart-matched elements (auto-detected from shot text) */}
                                                {(() => {
                                                    const dismissed = dismissedElements[shot.id] || new Set();
                                                    const smartMatchedElements = getElementsForShot(shot).filter(
                                                        elem => !shot.elementIds?.includes(elem.id) && !dismissed.has(elem.id)
                                                    );
                                                    return smartMatchedElements.map(elem => (
                                                        <span 
                                                            key={`smart-${elem.id}`}
                                                            className="group px-2 py-0.5 bg-yellow-600/20 text-yellow-300 rounded text-xs border border-yellow-600/40 border-dashed flex items-center gap-1"
                                                            title="Auto-detected from shot description. Click ✓ to confirm, × to dismiss."
                                                        >
                                                            <span className="text-yellow-500">⚡</span>
                                                            {elem.name}
                                                            <button
                                                                onClick={() => handleAddElementToShot(scene.id, shot.id, elem.id)}
                                                                className="hover:text-green-400 text-yellow-400"
                                                                title="Confirm this element"
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                onClick={() => setDismissedElements(prev => {
                                                                    const shotDismissed = new Set(prev[shot.id] || []);
                                                                    shotDismissed.add(elem.id);
                                                                    return { ...prev, [shot.id]: shotDismissed };
                                                                })}
                                                                className="hover:text-red-400 text-yellow-400"
                                                                title="Dismiss this suggestion"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ));
                                                })()}
                                                <select
                                                    onChange={(e) => {
                                                        if (e.target.value === '__new__') {
                                                            setEditingCharacterForShot({ sceneId: scene.id, shotId: shot.id, category: 'element' });
                                                            handleCreateNewCharacter('element');
                                                        } else if (e.target.value) {
                                                            handleAddElementToShot(scene.id, shot.id, e.target.value);
                                                        }
                                                        e.target.value = '';
                                                    }}
                                                    className="px-2 py-0.5 bg-gray-700/50 hover:bg-gray-700 text-gray-400 rounded text-xs border-0 cursor-pointer"
                                                    value=""
                                                >
                                                    <option value="">+ Add</option>
                                                    <option value="__new__" className="font-bold text-green-400">✨ Create New Element</option>
                                                    <option disabled>──────────</option>
                                                    {projectData.characters
                                                        .filter(c => c.category === 'element' && !shot.elementIds?.includes(c.id))
                                                        .map(elem => (
                                                            <option key={elem.id} value={elem.id}>{elem.name}</option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        </div>
                                        </>
                                        )}

                                        {/* Narration */}
                                        <div>
                                            <label className="text-xs font-semibold text-gray-400 mb-1 block uppercase">Description from Narration</label>
                                            <button
                                                onClick={() => setManagingAudioForShot(shot)}
                                                className="w-full py-2 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-md flex items-center justify-center gap-2 text-sm text-gray-300 transition-colors"
                                            >
                                                <MicrophoneIcon className="w-4 h-4" />
                                                Record / Upload to Transcribe
                                            </button>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="grid grid-cols-3 gap-2 pt-2">
                                            <button
                                                onClick={() => shot.video && setPreviewingVideoUrl({
                                                    url: shot.video,
                                                    trimStart: shot.trimStart,
                                                    trimEnd: shot.trimEnd,
                                                })}
                                                disabled={!shot.video}
                                                className="bg-violet-500/10 hover:bg-violet-500/20 disabled:bg-zinc-800 disabled:opacity-50 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                            >
                                                <PlayIcon className="w-3 h-3" />
                                                Preview
                                            </button>
                                            <button
                                                onClick={() => setPreppingVideoForShot(shot)}
                                                disabled={!hasGenerableVideoSource(shot)}
                                                className="bg-violet-500/10 hover:bg-violet-500/20 disabled:bg-zinc-800 disabled:opacity-50 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                                title={!hasGenerableVideoSource(shot)
                                                    ? ((shot.panels && shot.panels.length > 0)
                                                        ? 'Generate images for ALL panels first — video will use them as ordered references'
                                                        : 'Generate start frame first')
                                                    : 'Generate video'}
                                            >
                                                <FilmIcon className="w-3 h-3" />
                                                Gen Video
                                            </button>
                                            <button
                                                onClick={() => setAnimatingShot(shot)}
                                                disabled={!hasGenerableVideoSource(shot) || shot.video}
                                                className="bg-violet-500/10 hover:bg-violet-500/20 disabled:bg-gray-700 disabled:opacity-50 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                                title={!hasGenerableVideoSource(shot)
                                                    ? ((shot.panels && shot.panels.length > 0)
                                                        ? 'Generate images for ALL panels first'
                                                        : 'Generate start frame first')
                                                    : shot.video ? 'Video already exists - use Edit' : 'Motion control'}
                                                data-testid={`motion-control-btn-${shot.id}`}
                                            >
                                                🎬 Motion Ctrl
                                            </button>
                                            {shot.video && (
                                                <button
                                                    onClick={() => setEditingVideoForShot(shot)}
                                                    className="bg-cyan-600/50 hover:bg-cyan-600 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                                    title="Edit video"
                                                >
                                                    ✏️ Edit Video
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => videoInputRefs.current[shot.id]?.click()}
                                                className="bg-gray-700 hover:bg-gray-600 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                            >
                                                <UploadIcon className="w-3 h-3" />
                                                Upload Video
                                            </button>
                                            {shot.video && (
                                                <button
                                                    onClick={() => handleShotUpdate(scene.id, shot.id, 'video', null)}
                                                    className="bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                                >
                                                    <TrashIcon className="w-3 h-3" />
                                                    Delete Video
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* Add Scene Button */}
                <div className="w-[450px] flex-shrink-0">
                    <button
                        onClick={handleAddScene}
                        className="w-full h-32 border-2 border-dashed border-gray-700 hover:border-gray-600 rounded-lg flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-gray-400 transition-colors"
                    >
                        <PlusIcon className="w-8 h-8" />
                        <span className="font-semibold">Add Scene</span>
                    </button>
                </div>
            </div>

            {/* Modals */}
            {editingInfo && (
                <ImageEditorModal
                    shot={editingInfo.shot}
                    characters={typeof editingInfo.panelIndex === 'number'
                        ? [
                            // Per-panel cast/elements: union of panel-scoped + shot-fallback
                            ...getCharactersForPanel(editingInfo.shot, editingInfo.panelIndex),
                            ...getElementsForPanel(editingInfo.shot, editingInfo.panelIndex),
                        ]
                        : [
                            ...getCharactersForShot(editingInfo.shot),
                            ...(projectData.characters?.filter(c => c.category === 'element' && editingInfo.shot.elementIds?.includes(c.id)) || [])
                        ]}
                    projectData={projectData}
                    frameType={typeof editingInfo.panelIndex === 'number' ? 'start' : editingInfo.frame}
                    titleOverride={typeof editingInfo.panelIndex === 'number'
                        ? `Panel ${editingInfo.panelIndex + 1} — Shot ${editingInfo.shot.id}`
                        : undefined}
                    initialImage={typeof editingInfo.panelIndex === 'number'
                        ? (editingInfo.shot.panels?.[editingInfo.panelIndex]?.image || null)
                        : undefined}
                    initialPrompt={typeof editingInfo.panelIndex === 'number'
                        ? (editingInfo.shot.panels?.[editingInfo.panelIndex]?.prompt || '')
                        : undefined}
                    onClose={() => setEditingInfo(null)}
                    onSave={handleSaveFromImageEditor}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                    onSaveMedia={onSaveMedia}
                />
            )}

            {editingCharacter && (
                <CharacterPortraitModal
                    character={editingCharacter}
                    projectData={projectData}
                    onClose={() => {
                        setEditingCharacter(null);
                        setEditingCharacterForShot(null);
                        setPendingCreateForPanel(null);
                    }}
                    onSave={(charId, saveData) => {
                        // Merge saved data with existing character
                        const updatedChar = {
                            ...editingCharacter,
                            ...saveData,
                            id: charId
                        };
                        
                        if (editingCharacterForShot) {
                            // New character being created from shot
                            handleSaveCharacter(updatedChar, editingCharacterForShot.sceneId, editingCharacterForShot.shotId);
                            setEditingCharacterForShot(null);
                        } else if (pendingCreateForPanel) {
                            // Feb 2026 — New character/element being created from a PANEL
                            // Add dropdown. handleSaveCharacter reads pendingCreateForPanel
                            // and attaches the newly-saved entity to that panel atomically.
                            handleSaveCharacter(updatedChar);
                        } else {
                            // Existing character being edited
                            const updatedChars = projectData.characters.map(c => c.id === charId ? updatedChar : c);
                            onProjectUpdate({ ...projectData, characters: updatedChars });
                            setEditingCharacter(null);
                        }
                    }}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                />
            )}

            {preppingVideoForShot && (
                <VideoPreProductionModal
                    shot={preppingVideoForShot}
                    characters={getCharactersForShot(preppingVideoForShot)}
                    projectData={projectData}
                    onClose={() => setPreppingVideoForShot(null)}
                    onSave={handleSaveFromPreProduction}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                    onUpdateCharacter={(updatedChar) => {
                        const updatedChars = projectData.characters.map(c => c.id === updatedChar.id ? updatedChar : c);
                        onProjectUpdate({ ...projectData, characters: updatedChars });
                    }}
                />
            )}

            {animatingShot && (
                <AnimateVideoModal
                    shot={animatingShot}
                    projectData={projectData}
                    onClose={() => setAnimatingShot(null)}
                    onSave={handleSaveFromAnimation}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                />
            )}

            {editingVideoForShot && (
                <VideoEditorModal
                    shot={editingVideoForShot}
                    projectData={projectData}
                    onClose={() => setEditingVideoForShot(null)}
                    onSave={(updatedShot) => {
                        const scene = scenes.find(s => s.shots?.some(sh => sh.id === updatedShot.id));
                        if (scene) {
                            // Nov 2026 — Also propagate `trimStart`/`trimEnd`
                            // (metadata trim). Other edit tools only set
                            // `video`; trim only sets the range fields.
                            const updates = {};
                            if (updatedShot.video !== undefined) updates.video = updatedShot.video;
                            if (updatedShot.trimStart !== undefined) updates.trimStart = updatedShot.trimStart;
                            if (updatedShot.trimEnd !== undefined) updates.trimEnd = updatedShot.trimEnd;
                            handleShotUpdateMultiple(scene.id, updatedShot.id, updates);
                            // If only trim was applied (no new video URL), keep
                            // the editor open so the user can fine-tune. The
                            // "Current saved trim" chip in the Trim panel
                            // reflects the just-saved values.
                            const isTrimOnly = updates.video === undefined &&
                                (updates.trimStart !== undefined || updates.trimEnd !== undefined);
                            if (!isTrimOnly) setEditingVideoForShot(null);
                        }
                    }}
                    onDeductCredits={onDeductCredits}
                />
            )}

            {previewingVideoUrl && (
                <VideoPreviewModal
                    videoUrl={typeof previewingVideoUrl === 'string' ? previewingVideoUrl : previewingVideoUrl.url}
                    trimStart={typeof previewingVideoUrl === 'object' ? previewingVideoUrl.trimStart : undefined}
                    trimEnd={typeof previewingVideoUrl === 'object' ? previewingVideoUrl.trimEnd : undefined}
                    onClose={() => setPreviewingVideoUrl(null)}
                />
            )}

            {managingAudioForShot && (
                <ShotAudioModal
                    shot={managingAudioForShot}
                    onClose={() => setManagingAudioForShot(null)}
                    onDeductCredits={onDeductCredits}
                    onSave={(audio) => handleTranscribeAndSaveDescription(managingAudioForShot.id, audio)}
                />
            )}

            {/* Frame Image Viewing Modal */}
            {viewingFrameImage && (
                <div 
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                    onClick={() => setViewingFrameImage(null)}
                >
                    <div 
                        className="relative max-w-5xl max-h-[90vh] bg-gray-900 rounded-lg overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-700">
                            <h3 className="text-lg font-semibold text-white">{viewingFrameImage.title || 'Frame Image'}</h3>
                            <button
                                onClick={() => setViewingFrameImage(null)}
                                className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        {/* Image */}
                        <div className="p-4 flex items-center justify-center bg-black">
                            <img 
                                src={viewingFrameImage.url} 
                                alt={viewingFrameImage.title || 'Frame'}
                                className="max-w-full max-h-[70vh] object-contain"
                            />
                        </div>
                        {/* Footer with download button */}
                        <div className="flex justify-center gap-4 p-4 border-t border-gray-700">
                            <button
                                onClick={async () => {
                                    const url = viewingFrameImage.url;
                                    const rawTitle = viewingFrameImage.title || 'frame';
                                    const filename = `${String(rawTitle).replace(/[^a-zA-Z0-9-_]/g, '_')}.png`;
                                    try {
                                        // Cross-origin GCS URLs ignore <a download>; fetch as Blob then download
                                        if (url.startsWith('blob:') || url.startsWith('data:')) {
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = filename;
                                            document.body.appendChild(a);
                                            a.click();
                                            document.body.removeChild(a);
                                            return;
                                        }
                                        const r = await fetch(url, { credentials: 'omit' });
                                        if (!r.ok) throw new Error(`HTTP ${r.status}`);
                                        const blob = await r.blob();
                                        const blobUrl = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = blobUrl;
                                        a.download = filename;
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                    } catch (err) {
                                        console.error('Download failed:', err);
                                        window.open(url, '_blank', 'noopener,noreferrer');
                                    }
                                }}
                                data-testid="frame-image-download-btn"
                                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                            </button>
                            <button
                                onClick={() => setViewingFrameImage(null)}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Toolkit Settings Modal */}
            {showAIToolkitModal && (
                <div 
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={() => setShowAIToolkitModal(false)}
                >
                    <div 
                        className="relative w-full max-w-6xl max-h-[90vh] bg-[#0f0f17] rounded-2xl overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-700">
                            <div>
                                <h3 className="text-2xl font-bold text-white">AI Model Settings</h3>
                                <p className="text-gray-400 text-sm mt-1">Change AI models for image/video generation. Changes apply to new generations only.</p>
                            </div>
                            <button
                                onClick={() => setShowAIToolkitModal(false)}
                                className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                            >
                                <CloseIcon className="w-6 h-6 text-gray-400" />
                            </button>
                        </div>
                        {/* AI Toolkit Selector */}
                        <div className="flex-1 overflow-y-auto">
                            <AIToolkitSelector 
                                onContinue={() => setShowAIToolkitModal(false)}
                                onBack={() => setShowAIToolkitModal(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});
Storyboard.displayName = 'Storyboard';

// ──────────────────────────────────────────────────────────────────────
// PanelImageStrip — horizontal-scroll strip of panel image cards.
//
// New-project replacement for the legacy "Start Frame / End Frame" dual-card
// layout. Renders N cards where N = number of panels parsed from the shot's
// description (see `utils/panelParser.js`). Each card shows:
//   • Panel header (number + timecode)
//   • 16:9 image thumbnail (or a placeholder PhotoIcon)
//   • Generate / Upload / Edit-prompt / Delete actions
// All panels are displayed left-to-right in chronological order. The strip
// scrolls horizontally on narrow viewports so it works even when a shot has 6
// panels.
//
// Phase 1: image generation only. Phase 2 will pass these images in order as
// references to the references-to-video pipeline.
// ──────────────────────────────────────────────────────────────────────
const PanelImageStrip = ({
    shot,
    sceneId,
    panels,
    projectData,
    resolveCharacters,
    resolveElements,
    generatingFrames,
    onGenerate,
    onUploadClick,
    onUploadFile,
    onDelete,
    onEditPrompt,
    onEditImage,
    onViewImage,
    onAddCharacter,
    onRemoveCharacter,
    onAddElement,
    onRemoveElement,
    onEditCharacter,
    onCreateFromPanel, // Feb 2026 — opens the modal to create a new
                       // character/element and attach to this panel
    frameInputRefs,
}) => {
    const [editingPanelKey, setEditingPanelKey] = React.useState(null);
    const scrollerRef = React.useRef(null);

    if (!panels || panels.length === 0) {
        return (
            <div className="bg-white/5 border border-dashed border-white/15 rounded-lg p-4 text-center" data-testid={`panel-strip-empty-${shot.id}`}>
                <div className="text-xs text-zinc-400">
                    Panels will appear here once the shot description has a panel breakdown.
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                    Tip: click <span className="text-violet-300">"Edit Description"</span> above and paste the panel structure from the script (Panel 1, Panel 2, …).
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2 min-w-0" data-testid={`panel-strip-${shot.id}`}>
            <div className="flex items-center justify-between gap-2">
                <label className="text-xs font-semibold text-gray-400 uppercase">
                    Panel images <span className="text-zinc-500 font-normal normal-case">({panels.length})</span>
                </label>
                {panels.length > 1 && (
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => { if (scrollerRef.current) scrollerRef.current.scrollBy({ left: -220, behavior: 'smooth' }); }}
                            className="w-6 h-6 rounded bg-white/5 hover:bg-white/15 border border-white/10 text-zinc-300 flex items-center justify-center"
                            title="Scroll panels left"
                            data-testid={`panel-strip-scroll-left-${shot.id}`}
                        >
                            <ArrowLeftIcon className="w-3 h-3" />
                        </button>
                        <button
                            type="button"
                            onClick={() => { if (scrollerRef.current) scrollerRef.current.scrollBy({ left: 220, behavior: 'smooth' }); }}
                            className="w-6 h-6 rounded bg-white/5 hover:bg-white/15 border border-white/10 text-zinc-300 flex items-center justify-center"
                            title="Scroll panels right"
                            data-testid={`panel-strip-scroll-right-${shot.id}`}
                        >
                            <ArrowRightIcon className="w-3 h-3" />
                        </button>
                    </div>
                )}
            </div>
            <div
                ref={scrollerRef}
                className="panel-strip-scroller flex gap-3 overflow-x-auto overflow-y-hidden pb-2"
                style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(139, 92, 246, 0.5) rgba(255,255,255,0.05)',
                    WebkitOverflowScrolling: 'touch',
                    maxWidth: '100%',
                }}
            >                {panels.map((panel, panelIndex) => {
                    const key = `${shot.id}-panel-${panelIndex}`;
                    const isGenerating = !!generatingFrames[key];
                    const isEditing = editingPanelKey === `${shot.id}-${panelIndex}`;
                    return (
                        <div
                            key={panel.id || panelIndex}
                            className="shrink-0 flex-none w-52 space-y-2"
                            data-testid={`panel-card-${shot.id}-${panelIndex}`}
                        >
                            <div className="flex items-center justify-between text-[11px]">
                                <span className="text-violet-300 font-semibold">Panel {panelIndex + 1}</span>
                                {panel.timecode && (
                                    <span className="text-zinc-500 font-mono">{panel.timecode}</span>
                                )}
                            </div>

                            <div className="relative group aspect-video bg-gray-900 rounded-md overflow-hidden">
                                {panel.image ? (
                                    <>
                                        <img
                                            src={panel.image}
                                            alt={`Panel ${panelIndex + 1}`}
                                            className="w-full h-full object-cover"
                                            crossOrigin="anonymous"
                                            referrerPolicy="no-referrer"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button
                                                type="button"
                                                onClick={() => onViewImage(panelIndex, panel.image)}
                                                className="p-2 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300"
                                                title="View full image"
                                                data-testid={`panel-view-btn-${shot.id}-${panelIndex}`}
                                            >
                                                <EyeIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <PhotoIcon className="w-8 h-8 text-gray-600" />
                                    </div>
                                )}
                                {isGenerating && (
                                    <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                                        <LoadingSpinner className="w-6 h-6" />
                                    </div>
                                )}
                            </div>

                            <div className="min-h-[2rem]">
                                {isEditing ? (
                                    <div className="space-y-1">
                                        <textarea
                                            defaultValue={panel.prompt || ''}
                                            rows={3}
                                            autoFocus
                                            className="w-full p-1.5 bg-black/20 border border-violet-500/30 rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-violet-500/30"
                                            id={`panel-prompt-${shot.id}-${panelIndex}`}
                                            data-testid={`panel-prompt-textarea-${shot.id}-${panelIndex}`}
                                            onKeyDown={(e) => { if (e.key === 'Escape') setEditingPanelKey(null); }}
                                        />
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const val = document.getElementById(`panel-prompt-${shot.id}-${panelIndex}`)?.value;
                                                    if (val !== undefined) onEditPrompt(sceneId, shot.id, panelIndex, val);
                                                    setEditingPanelKey(null);
                                                }}
                                                className="px-2 py-0.5 bg-violet-600 hover:bg-violet-700 text-white text-[10px] rounded font-semibold"
                                            >Save</button>
                                            <button
                                                type="button"
                                                onClick={() => setEditingPanelKey(null)}
                                                className="px-2 py-0.5 bg-gray-700 hover:bg-gray-600 text-white text-[10px] rounded"
                                            >Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <p
                                        className="text-xs text-gray-500 line-clamp-2 cursor-pointer hover:text-gray-300 transition-colors"
                                        onClick={() => setEditingPanelKey(`${shot.id}-${panelIndex}`)}
                                        title="Click to edit prompt"
                                        data-testid={`panel-prompt-display-${shot.id}-${panelIndex}`}
                                    >
                                        {panel.prompt || <span className="italic">No prompt yet</span>}
                                    </p>
                                )}
                            </div>

                            <div className="flex gap-1">
                                <button
                                    type="button"
                                    onClick={() => onGenerate(sceneId, shot.id, panelIndex)}
                                    disabled={isGenerating || !panel.prompt}
                                    className="flex-1 bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/20 disabled:bg-zinc-800 text-white text-xs py-1 rounded flex items-center justify-center"
                                    title="Generate panel image"
                                    data-testid={`panel-gen-btn-${shot.id}-${panelIndex}`}
                                >
                                    <MagicWandIcon className="w-3 h-3 mr-1" />
                                    Gen
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onUploadClick(panelIndex)}
                                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs py-1 rounded flex items-center justify-center"
                                    title="Upload panel image"
                                    data-testid={`panel-upload-btn-${shot.id}-${panelIndex}`}
                                >
                                    <UploadIcon className="w-3 h-3 mr-1" />
                                    Up
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onEditImage(panelIndex)}
                                    className="flex-1 bg-violet-500/10 hover:bg-violet-500/20 text-white text-xs py-1 rounded flex items-center justify-center"
                                    title="Edit panel image (refine with prompt + reference images)"
                                    data-testid={`panel-edit-btn-${shot.id}-${panelIndex}`}
                                >
                                    <PencilIcon className="w-3 h-3 mr-1" />
                                    Ed
                                </button>
                                {panel.image && (
                                    <button
                                        type="button"
                                        onClick={() => onDelete(sceneId, shot.id, panelIndex)}
                                        className="bg-red-600/50 hover:bg-red-600 text-white text-xs py-1 px-2 rounded"
                                        title="Delete panel image"
                                        data-testid={`panel-delete-btn-${shot.id}-${panelIndex}`}
                                    >
                                        <TrashIcon className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Per-panel CHARACTERS chips */}
                            <div className="pt-1">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Characters</label>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {(() => {
                                        // Resolved IDs reflect: explicit override → auto-detect from description → shot-level fallback
                                        const resolvedChars = resolveCharacters ? resolveCharacters(panelIndex) : [];
                                        const resolvedIds = resolvedChars.map(c => c.id);
                                        const isAutoDetected = !(panel.characterIds && panel.characterIds.length > 0);
                                        return resolvedChars.map(char => (
                                            <span
                                                key={char.id}
                                                className={`group px-1.5 py-0.5 rounded text-[10px] border flex items-center gap-1 cursor-pointer ${
                                                    isAutoDetected
                                                        ? 'bg-violet-500/10 text-violet-300 border-violet-500/20 border-dashed'
                                                        : 'bg-violet-500/20 text-violet-300 border-violet-500/30 hover:bg-violet-500/30'
                                                }`}
                                                onClick={() => onEditCharacter(char)}
                                                title={isAutoDetected ? `Auto-detected from panel description. Click × to remove.` : 'Click to edit character'}
                                                data-testid={`panel-char-chip-${shot.id}-${panelIndex}-${char.id}`}
                                            >
                                                {isAutoDetected && <span className="text-violet-400/70">⚡</span>}
                                                {char.name}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRemoveCharacter(sceneId, shot.id, panelIndex, char.id);
                                                    }}
                                                    className="opacity-0 group-hover:opacity-100 hover:text-red-400"
                                                >×</button>
                                            </span>
                                        )).concat(
                                            <select
                                                key={`add-char-${shot.id}-${panelIndex}`}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === '__create_new__') {
                                                        onCreateFromPanel(sceneId, shot.id, panelIndex, 'character');
                                                    } else if (val) {
                                                        onAddCharacter(sceneId, shot.id, panelIndex, val);
                                                    }
                                                    e.target.value = '';
                                                }}
                                                className="px-1.5 py-0.5 bg-gray-700/50 hover:bg-gray-700 text-gray-400 rounded text-[10px] border-0 cursor-pointer max-w-[80px]"
                                                value=""
                                                data-testid={`panel-char-add-${shot.id}-${panelIndex}`}
                                            >
                                                <option value="">+ Add</option>
                                                {(projectData?.characters || [])
                                                    .filter(c => (c.category ?? 'character') === 'character')
                                                    .filter(c => !resolvedIds.includes(c.id))
                                                    .map(char => (
                                                        <option key={char.id} value={char.id}>{char.name}</option>
                                                    ))}
                                                <option value="__create_new__" data-testid={`panel-char-create-new-${shot.id}-${panelIndex}`}>
                                                    ✨ Create new character…
                                                </option>
                                            </select>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Per-panel ELEMENTS chips */}
                            <div>
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Other elements</label>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {(() => {
                                        const resolvedElems = resolveElements ? resolveElements(panelIndex) : [];
                                        const resolvedIds = resolvedElems.map(c => c.id);
                                        const isAutoDetected = !(panel.elementIds && panel.elementIds.length > 0);
                                        return resolvedElems.map(elem => (
                                            <span
                                                key={elem.id}
                                                className={`group px-1.5 py-0.5 rounded text-[10px] border flex items-center gap-1 cursor-pointer ${
                                                    isAutoDetected
                                                        ? 'bg-cyan-600/15 text-cyan-300 border-cyan-600/30 border-dashed'
                                                        : 'bg-cyan-600/30 text-cyan-300 border-cyan-600/50 hover:bg-cyan-600/40'
                                                }`}
                                                onClick={() => onEditCharacter(elem)}
                                                data-testid={`panel-elem-chip-${shot.id}-${panelIndex}-${elem.id}`}
                                                title={isAutoDetected ? 'Auto-detected from panel description. Click to edit, or × to remove.' : 'Click to edit element'}
                                            >
                                                {isAutoDetected && <span className="text-cyan-400/70">⚡</span>}
                                                {elem.name}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRemoveElement(sceneId, shot.id, panelIndex, elem.id);
                                                    }}
                                                    className="opacity-0 group-hover:opacity-100 hover:text-red-400"
                                                    title="Remove from panel"
                                                >×</button>
                                            </span>
                                        )).concat(
                                            <select
                                                key={`add-elem-${shot.id}-${panelIndex}`}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === '__create_new__') {
                                                        onCreateFromPanel(sceneId, shot.id, panelIndex, 'element');
                                                    } else if (val) {
                                                        onAddElement(sceneId, shot.id, panelIndex, val);
                                                    }
                                                    e.target.value = '';
                                                }}
                                                className="px-1.5 py-0.5 bg-gray-700/50 hover:bg-gray-700 text-gray-400 rounded text-[10px] border-0 cursor-pointer max-w-[80px]"
                                                value=""
                                                data-testid={`panel-elem-add-${shot.id}-${panelIndex}`}
                                            >
                                                <option value="">+ Add</option>
                                                {(projectData?.characters || [])
                                                    .filter(c => c.category === 'element')
                                                    .filter(c => !resolvedIds.includes(c.id))
                                                    .map(elem => (
                                                        <option key={elem.id} value={elem.id}>{elem.name}</option>
                                                    ))}
                                                <option value="__create_new__" data-testid={`panel-elem-create-new-${shot.id}-${panelIndex}`}>
                                                    ✨ Create new element…
                                                </option>
                                            </select>
                                        );
                                    })()}
                                </div>
                            </div>

                            <input
                                ref={(el) => { if (el) frameInputRefs.current[`${shot.id}-panel-${panelIndex}`] = el; }}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => onUploadFile(e, sceneId, shot.id, panelIndex)}
                                data-testid={`panel-file-input-${shot.id}-${panelIndex}`}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
